import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import EventCard from "../shared/EventCard";
import AdminDrawer from "./AdminDrawer";
import { supabase } from "../../supabaseClient";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const FULL_DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ── Helpers ───────────────────────────────────────────────────────────────────

export function getToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function getTomorrow() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function fmt(time) {
  if (!time) return "";
  const [h, m] = time.split(":");
  const hr = parseInt(h, 10);
  return `${hr > 12 ? hr - 12 : hr === 0 ? 12 : hr}:${m} ${hr >= 12 ? "PM" : "AM"}`;
}

export function isHead(profile) {
  return profile?.staff_type === "head" || profile?.staff_type === "lead" || profile?.role === "manager";
}


// ── Internal helpers ──────────────────────────────────────────────────────────

function toDateString(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getAssignedProfiles(event, eventStaff, allProfiles) {
  return eventStaff
    .filter((es) => es.event_id === event.id)
    .map((es) => allProfiles.find((p) => p.id === es.employee_id))
    .filter(Boolean);
}

function getEventStatus(event, eventStaff, allProfiles) {
  const assigned = getAssignedProfiles(event, eventStaff, allProfiles);
  if (assigned.length === 0) return "unassigned";
  const managerCount = assigned.filter(p => p.role === "manager").length;
  const leadCount = assigned.filter(p => isHead(p) && p.role !== "manager").length;
  const qualifiedCount = managerCount + leadCount;
  if (qualifiedCount === 0) return "missing-head";
  if (managerCount >= 2) return "double-head";
  return "covered";
}

const FLAG_LABELS = {
  covered: null,
  "missing-head": "Needs Lead",
  "double-head": "2 Managers",
  unassigned: "Unassigned",
};

function buildDays(events, eventStaff, allProfiles, count = 28, currentLocation = "") {
  const today = getToday();
  const base = new Date(today + "T00:00:00");
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    const date = toDateString(d);

    let status = "covered";
    if (date === today) {
      const nowHHMM = (() => { const n = new Date(); return `${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`; })();
      const dayEvents = events
        .filter((e) => e.date === date && (!e.location || e.location === currentLocation))
        .filter((e) => !(e.end_time && nowHHMM >= e.end_time));
      if (dayEvents.length === 0) {
        status = "today-empty";
      } else {
        let hasGap = false;
        let hasWarn = false;
        for (const ev of dayEvents) {
          const assigned = getAssignedProfiles(ev, eventStaff, allProfiles);
          const managerCount = assigned.filter(p => p.role === "manager").length;
          const leadCount = assigned.filter(p => isHead(p) && p.role !== "manager").length;
          const qualifiedCount = managerCount + leadCount;
          if (qualifiedCount === 0) hasGap = true;
          if (managerCount >= 2) hasWarn = true;
        }
        if (hasGap) status = "today-gap";
        else if (hasWarn) status = "today-warn";
        else status = "today-covered";
      }
    } else {
      const dayEvents = events.filter((e) => e.date === date && (!e.location || e.location === currentLocation));
      if (dayEvents.length === 0) {
        status = "empty";
      } else {
        let hasGap = false;
        let hasWarn = false;
        for (const ev of dayEvents) {
          const assigned = getAssignedProfiles(ev, eventStaff, allProfiles);
          const managerCount = assigned.filter(p => p.role === "manager").length;
          const leadCount = assigned.filter(p => isHead(p) && p.role !== "manager").length;
          const qualifiedCount = managerCount + leadCount;
          if (qualifiedCount === 0) hasGap = true;
          if (managerCount >= 2) hasWarn = true;
        }
        if (hasGap) status = "has-gap";
        else if (hasWarn) status = "has-warn";
        else status = "covered";
      }
    }

    return { date, dayLabel: DAY_LABELS[d.getDay()], dayNum: d.getDate(), status };
  });
}

function getOrdinal(n) {
  const s = ["th","st","nd","rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const FULL_MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function fmtReqDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return `${DAY_LABELS[d.getDay()]} ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

function fmtShortDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

function formatSelectedSummary(selectedDates) {
  if (!selectedDates || selectedDates.size === 0) return null;
  const sorted = [...selectedDates].sort();
  const count = sorted.length;
  const labels = sorted.map(fmtReqDate);
  const shown = labels.slice(0, 3).join(", ");
  const extra = count > 3 ? ` and ${count - 3} more` : "";
  return `${count} selected — ${shown}${extra}`;
}

function formatFullDate(date) {
  const d = new Date(date + "T00:00:00");
  return `${FULL_DAY_NAMES[d.getDay()]}, ${FULL_MONTH_NAMES[d.getMonth()]} ${getOrdinal(d.getDate())}`;
}

function formatDayHeadline(date) {
  const today = getToday();
  const tomorrow = getTomorrow();
  if (date === today) return "Today";
  if (date === tomorrow) return "Tomorrow";
  const d = new Date(date + "T00:00:00");
  return `${FULL_DAY_NAMES[d.getDay()]}, ${FULL_MONTH_NAMES[d.getMonth()]} ${d.getDate()}`;
}

function getDayStatusPill(dayEvents, eventStaff, allProfiles) {
  if (dayEvents.length === 0) {
    return { label: "No Events", cls: "bg-gray-100 text-gray-400" };
  }
  let needsHead = false;
  let hasWarn = false;
  for (const ev of dayEvents) {
    const s = getEventStatus(ev, eventStaff, allProfiles);
    if (s === "missing-head" || s === "unassigned") needsHead = true;
    if (s === "double-head") hasWarn = true;
  }
  if (needsHead) return { label: "Missing Lead", cls: "bg-brand-red-soft text-brand-red" };
  if (hasWarn) return { label: "2 Managers", cls: "bg-brand-warn-soft text-brand-warn" };
  return { label: "All Covered", cls: "bg-brand-green-soft text-brand-green" };
}

// ── TrashIcon ─────────────────────────────────────────────────────────────────

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1.75 3.5h10.5M5.25 3.5V2.333a.583.583 0 0 1 .583-.583h2.334a.583.583 0 0 1 .583.583V3.5M11.083 3.5l-.583 7.583a.583.583 0 0 1-.583.584H4.083a.583.583 0 0 1-.583-.584L2.917 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5.833 6.417v2.916M8.167 6.417v2.916" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

// ── DrumPicker ────────────────────────────────────────────────────────────────

function DrumPicker({ value, onChange, label, dark = false }) {
  const ITEM_H = 44;
  const MINS = [0, 15, 30, 45];
  const [localH, setLocalH] = useState(() => parseInt(value.split(":")[0], 10));
  const [localM, setLocalM] = useState(() => {
    const raw = parseInt(value.split(":")[1], 10);
    return MINS.reduce((best, m) => Math.abs(m - raw) < Math.abs(best - raw) ? m : best, 0);
  });
  const hourRef = useRef(null);
  const minRef = useRef(null);
  const ampmRef = useRef(null);
  const hourTimer = useRef(null);
  const minTimer = useRef(null);
  const ampmTimer = useRef(null);
  const localHRef = useRef(localH);
  const localMRef = useRef(localM);
  useEffect(() => { localHRef.current = localH; }, [localH]);
  useEffect(() => { localMRef.current = localM; }, [localM]);

  useEffect(() => {
    if (hourRef.current) hourRef.current.scrollTop = localH * ITEM_H;
    if (minRef.current) minRef.current.scrollTop = MINS.indexOf(localM) * ITEM_H;
    if (ampmRef.current) ampmRef.current.scrollTop = (localH >= 12 ? 1 : 0) * ITEM_H;
  }, []); // eslint-disable-line

  useEffect(() => {
    const h = parseInt(value.split(":")[0], 10);
    const mRaw = parseInt(value.split(":")[1], 10);
    const m = MINS.reduce((best, mn) => Math.abs(mn - mRaw) < Math.abs(best - mRaw) ? mn : best, 0);
    if (h !== localHRef.current || m !== localMRef.current) {
      setLocalH(h); setLocalM(m); localHRef.current = h; localMRef.current = m;
      if (hourRef.current) hourRef.current.scrollTop = h * ITEM_H;
      if (minRef.current) minRef.current.scrollTop = MINS.indexOf(m) * ITEM_H;
    }
    if (ampmRef.current) ampmRef.current.scrollTop = (h >= 12 ? 1 : 0) * ITEM_H;
  }, [value]); // eslint-disable-line

  useEffect(() => {
    const addDrag = (el) => {
      if (!el) return;
      let startY = 0;
      let startScroll = 0;
      let isDragging = false;

      const onMouseDown = (e) => {
        isDragging = true;
        startY = e.clientY;
        startScroll = el.scrollTop;
        e.preventDefault();
      };

      const onMouseMove = (e) => {
        if (!isDragging) return;
        const delta = startY - e.clientY;
        el.scrollTop = startScroll + delta;
      };

      const onMouseUp = () => {
        if (!isDragging) return;
        isDragging = false;
        const idx = Math.round(el.scrollTop / 44);
        el.scrollTop = idx * 44;
      };

      el.addEventListener("mousedown", onMouseDown);
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);

      return () => {
        el.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };
    };

    const hourEl = hourRef.current;
    const minEl = minRef.current;
    const ampmEl = ampmRef.current;

    const cleanH = addDrag(hourEl);
    const cleanM = addDrag(minEl);
    const cleanA = addDrag(ampmEl);

    return () => {
      cleanH?.();
      cleanM?.();
      cleanA?.();
    };
  }, []); // eslint-disable-line

  function onHourScroll() {
    clearTimeout(hourTimer.current);
    hourTimer.current = setTimeout(() => {
      if (!hourRef.current) return;
      const idx = Math.round(hourRef.current.scrollTop / ITEM_H);
      const newH = Math.max(0, Math.min(23, idx));
      setLocalH(newH); localHRef.current = newH;
      onChange(`${String(newH).padStart(2, "0")}:${String(localMRef.current).padStart(2, "0")}`);
    }, 100);
  }

  function onMinScroll() {
    clearTimeout(minTimer.current);
    minTimer.current = setTimeout(() => {
      if (!minRef.current) return;
      const idx = Math.round(minRef.current.scrollTop / ITEM_H);
      const newMIdx = Math.max(0, Math.min(3, idx));
      const newM = MINS[newMIdx];
      setLocalM(newM); localMRef.current = newM;
      onChange(`${String(localHRef.current).padStart(2, "0")}:${String(newM).padStart(2, "0")}`);
    }, 100);
  }

  function onAmpmScroll() {
    clearTimeout(ampmTimer.current);
    ampmTimer.current = setTimeout(() => {
      if (!ampmRef.current) return;
      const idx = Math.max(0, Math.min(1, Math.round(ampmRef.current.scrollTop / ITEM_H)));
      ampmRef.current.scrollTop = idx * ITEM_H;
      const wantPm = idx === 1;
      const isPm = localHRef.current >= 12;
      if (wantPm !== isPm) {
        const newH = wantPm ? localHRef.current + 12 : localHRef.current - 12;
        setLocalH(newH); localHRef.current = newH;
        if (hourRef.current) hourRef.current.scrollTop = newH * ITEM_H;
        onChange(`${String(newH).padStart(2, "0")}:${String(localMRef.current).padStart(2, "0")}`);
      }
    }, 100);
  }

  const colStyle = {
    height: ITEM_H * 3, overflowY: "scroll", scrollSnapType: "y mandatory",
    flex: 1, position: "relative", zIndex: 2,
    touchAction: "pan-y",
    WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none",
    cursor: "grab", userSelect: "none",
  };

  return (
    <div className="drum-picker" data-no-pull>
      {label && <div style={{ fontSize: 11, fontWeight: 700, color: dark ? "#fff" : "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, textAlign: "center" }}>{label}</div>}
      <div style={{ position: "relative", display: "flex", alignItems: "center", borderRadius: 12, overflow: "hidden", border: dark ? "0.5px solid #333" : "1.5px solid #EFEFEF", background: dark ? "#222" : "#fafafa" }}>
        <div style={{ position: "absolute", left: 0, right: 0, top: ITEM_H, height: ITEM_H, background: dark ? "#333" : "#EFEFEF", pointerEvents: "none", zIndex: 1 }} />
        <div ref={hourRef} onScroll={onHourScroll} style={colStyle} onMouseDown={e => { e.currentTarget.style.cursor = "grabbing"; }} onMouseUp={e => { e.currentTarget.style.cursor = "grab"; }}>
          <div style={{ paddingTop: ITEM_H, paddingBottom: ITEM_H }}>
            {Array.from({ length: 24 }, (_, i) => i).map(hr => {
              const h12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
              return (
                <div key={hr} style={{ height: ITEM_H, display: "flex", alignItems: "center", justifyContent: "center", scrollSnapAlign: "center", fontSize: 20, fontWeight: hr === localH ? 700 : 400, color: hr === localH ? "#111" : "#ccc" }}>
                  {h12}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#111", zIndex: 2, padding: "0 4px" }}>:</div>
        <div ref={minRef} onScroll={onMinScroll} style={colStyle} onMouseDown={e => { e.currentTarget.style.cursor = "grabbing"; }} onMouseUp={e => { e.currentTarget.style.cursor = "grab"; }}>
          <div style={{ paddingTop: ITEM_H, paddingBottom: ITEM_H }}>
            {MINS.map(mn => (
              <div key={mn} style={{ height: ITEM_H, display: "flex", alignItems: "center", justifyContent: "center", scrollSnapAlign: "center", fontSize: 20, fontWeight: mn === localM ? 700 : 400, color: mn === localM ? "#111" : "#ccc" }}>
                {String(mn).padStart(2, "0")}
              </div>
            ))}
          </div>
        </div>
        <div ref={ampmRef} onScroll={onAmpmScroll} style={{ ...colStyle, flex: "none", width: 64, scrollSnapType: "none" }} onMouseDown={e => { e.currentTarget.style.cursor = "grabbing"; }} onMouseUp={e => { e.currentTarget.style.cursor = "grab"; }}>
          <div style={{ paddingTop: ITEM_H, paddingBottom: ITEM_H }}>
            {["AM", "PM"].map((period, idx) => {
              const selected = (localH < 12 ? 0 : 1) === idx;
              return (
                <div key={period} style={{ height: ITEM_H, display: "flex", alignItems: "center", justifyContent: "center", scrollSnapAlign: "center", fontSize: 16, fontWeight: selected ? 700 : 400, color: selected ? "#111" : "#ccc" }}>
                  {period}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── InfiniteFormDateStrip ─────────────────────────────────────────────────────

function InfiniteFormDateStrip({ selectedDates, onToggle, blockedDates = new Set(), conflictDates = new Set() }) {
  const today = getToday();
  const [dayCount, setDayCount] = React.useState(60);
  const scrollRef = React.useRef(null);
  const [todayVisible, setTodayVisible] = React.useState(true);
  const isDragging = React.useRef(false);
  const dragStartX = React.useRef(0);
  const dragScrollLeft = React.useRef(0);
  const dragMoved = React.useRef(false);
  const hasSelection = selectedDates.size > 0;
  const firstSelected = hasSelection ? [...selectedDates].sort()[0] : null;
  const showBackToToday = !todayVisible || (firstSelected && firstSelected !== today);

  function handleScroll() {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollWidth - el.scrollLeft - el.clientWidth < 300) setDayCount(c => c + 60);
    setTodayVisible(el.scrollLeft < 56);
  }

  function snapToToday() {
    if (scrollRef.current) { scrollRef.current.scrollLeft = 0; }
    setTodayVisible(true);
    if (!selectedDates.has(today)) onToggle(today);
  }

  const days = Array.from({ length: dayCount }, (_, i) => {
    const d = new Date(today + "T00:00:00");
    d.setDate(d.getDate() + i);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { dateStr, dayLabel: DAY_LABELS[d.getDay()], dayNum: d.getDate(), monthLabel: MONTH_SHORT[d.getMonth()], year: d.getFullYear() };
  });

  return (
    <div data-no-pull style={{ overflow: "visible" }}>
      {/* Back to Today pill */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4, minHeight: 22 }}>
        {showBackToToday && (
          <button type="button" onClick={snapToToday}
            style={{ fontSize: 11, fontWeight: 600, color: "#555", background: "white", border: "1px solid #DDD", borderRadius: 20, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", WebkitTapHighlightColor: "transparent" }}>
            ← Back to Today
          </button>
        )}
      </div>
      <div ref={scrollRef} onScroll={handleScroll}
        onMouseDown={e => { isDragging.current = true; dragMoved.current = false; dragStartX.current = e.pageX - scrollRef.current.offsetLeft; dragScrollLeft.current = scrollRef.current.scrollLeft; scrollRef.current.style.cursor = "grabbing"; }}
        onMouseMove={e => { if (!isDragging.current) return; const x = e.pageX - scrollRef.current.offsetLeft; const walk = x - dragStartX.current; if (Math.abs(walk) > 4) dragMoved.current = true; scrollRef.current.scrollLeft = dragScrollLeft.current - walk; }}
        onMouseUp={() => { isDragging.current = false; scrollRef.current.style.cursor = "grab"; }}
        onMouseLeave={() => { isDragging.current = false; if (scrollRef.current) scrollRef.current.style.cursor = "grab"; }}
        style={{ display: "flex", gap: 6, overflowX: "auto", overflowY: "visible", paddingBottom: 4, WebkitOverflowScrolling: "touch", scrollbarWidth: "none", cursor: "grab", userSelect: "none" }}>
        <style>{`.iform-strip::-webkit-scrollbar{display:none}`}</style>
        {days.map(({ dateStr, dayLabel, dayNum, monthLabel, year }) => {
          const isTodayTile = dateStr === today;
          const sel = selectedDates.has(dateStr);
          const blocked = blockedDates.has(dateStr);
          const conflict = conflictDates.has(dateStr);
          const disabled = blocked || conflict;

          let bg = "#f4f4f4";
          let numColor = "#111";
          let labelColor = "#888";
          let border = "none";

          if (isTodayTile && !sel) { bg = "#0A0A0A"; numColor = "white"; labelColor = "rgba(255,255,255,0.6)"; }
          else if (conflict) { bg = "#FFEBEA"; numColor = "#E53935"; labelColor = "#E53935"; border = "1.5px solid #E53935"; }
          else if (blocked) { bg = "#f0f0f0"; }
          else if (sel) { bg = "#F55C00"; numColor = "white"; labelColor = "rgba(255,255,255,0.8)"; }
          if (sel && !isTodayTile && !conflict) border = "2px solid #0A0A0A";

          return (
            <div key={dateStr} style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
              {/* Month label row */}
              <div style={{ height: 18, display: "flex", alignItems: "center", marginBottom: 2 }}>
                {dayNum === 1 && (
                  <span style={{ fontSize: 10, fontWeight: 600, color: "#111", textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>
                    {monthLabel} {year}
                  </span>
                )}
              </div>
              {/* Tile */}
              <div onClick={() => { if (!disabled && !dragMoved.current) onToggle(dateStr); }} data-no-pull
                style={{ width: 52, height: 52, borderRadius: 10, background: bg, border, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: disabled ? "default" : "pointer", opacity: blocked ? 0.45 : 1, position: "relative" }}>
                <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: labelColor }}>{dayLabel}</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: numColor, lineHeight: 1, marginTop: 2 }}>{dayNum}</span>
                {conflict && <span style={{ fontSize: 8, fontWeight: 700, color: "#E53935", marginTop: 1 }}>!</span>}
                {blocked && !conflict && <span style={{ fontSize: 8, fontWeight: 700, color: "#888", marginTop: 1 }}>OFF</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── RecurringCalendar ─────────────────────────────────────────────────────────

function RecurringCalendar({ selectedWeekdays, minDate, blockedDates = new Set(), value, onChange, calMonth, onChangeMonth }) {
  const today = minDate || getToday();
  const { year, month } = calMonth;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const startPad = new Date(year, month, 1).getDay();

  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) {
    const mm = String(month + 1).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    const dateStr = `${year}-${mm}-${dd}`;
    const dow = new Date(year, month, d).getDay();
    cells.push({ d, dateStr, isActive: selectedWeekdays.has(dow) && dateStr >= minDate && !blockedDates?.has(dateStr), isSelected: value === dateStr });
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <button type="button" onClick={() => onChangeMonth(-1)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#111", padding: "4px 10px" }}>‹</button>
        <span style={{ fontWeight: 700, fontSize: 12, color: "#111" }}>{FULL_MONTH_NAMES[month]} {year}</span>
        <button type="button" onClick={() => onChangeMonth(1)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#111", padding: "4px 10px" }}>›</button>
      </div>
      <div style={{ maxWidth: window.innerWidth >= 768 ? 340 : 280, margin: "0 auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
        {DAY_LABELS.map(d => <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase" }}>{d}</div>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, rowGap: 2 }}>
        {cells.map((cell, i) => {
          if (!cell) return <div key={`p${i}`} />;
          return (
            <div key={cell.dateStr} onClick={() => cell.isActive && onChange(cell.dateStr)} style={{ height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", background: cell.isSelected ? "#F55C00" : "transparent", color: cell.isSelected ? "white" : cell.isActive ? "#111" : "#ddd", fontWeight: cell.isSelected ? 700 : 500, fontSize: 11, cursor: cell.isActive ? "pointer" : "default" }}>
              {cell.d}
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}

// ── ConfirmModal ──────────────────────────────────────────────────────────────

function ConfirmModal({ title, message, subtitle, confirmLabel, onConfirm, onCancel, busy }) {
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-end justify-center"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      onTouchStart={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="w-full bg-white rounded-t-2xl px-6 pt-4 pb-8"
        style={{ animation: "slideUp 200ms ease" }}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center mb-5">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>
        <h3 className="text-[18px] font-bold text-brand-black mb-1">{title}</h3>
        {message && <p className="text-[13px] text-brand-text-soft mb-1">{message}</p>}
        {subtitle && <div className="mb-5">{subtitle}</div>}
        {!message && !subtitle && <div className="mb-5" />}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="w-full rounded-xl py-3.5 text-[15px] font-bold text-white bg-brand-red disabled:opacity-50 transition-opacity"
          >
            {busy ? "Working…" : confirmLabel}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="w-full rounded-xl py-3.5 text-[15px] font-semibold text-brand-black border border-brand-border bg-white"
          >
            Keep it
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Shared input style ────────────────────────────────────────────────────────

const inputCls =
  "w-full rounded-xl border border-brand-border bg-gray-50 px-4 py-3 text-[14px] text-brand-black font-sans appearance-none focus:outline-none focus:border-brand-black transition-colors";

// ── Component ─────────────────────────────────────────────────────────────────

export default function ManagerLineup({
  user,
  shifts = [],
  events = [],
  eventStaff = [],
  allProfiles = [],
  requests = [],
  allLocations = [],
  pendingProfiles = [],
  onShiftAdded,
  onHandleRequest,
  onInvite = () => {},
  onEditLocations = () => {},
  onEditName = () => {},
  onChangeRole = () => {},
  onRemove = () => {},
  onResendInvite = () => {},
  onMyTeam,
  currentLocation = "",
  onLocationChange,
}) {
  const tomorrow = getTomorrow();
  const [selectedDate, setSelectedDate] = useState(tomorrow);
  const [nowHHMM, setNowHHMM] = useState(() => { const n = new Date(); return `${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`; });
  useEffect(() => {
    const tick = setInterval(() => { const n = new Date(); setNowHHMM(`${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`); }, 60000);
    return () => clearInterval(tick);
  }, []);
  const [stripDayCount, setStripDayCount] = useState(365);
  const stripScrollRef = useRef(null);
  const stripIsDragging = React.useRef(false);
  const stripDragStartX = React.useRef(0);
  const stripDragScrollLeft = React.useRef(0);
  const stripDragMoved = React.useRef(false);
  const [stripTodayVisible, setStripTodayVisible] = useState(true);
  const [stripCalMode, setStripCalMode] = useState("week"); // "week" | "month"
  const [stripMonthOffset, setStripMonthOffset] = useState(0); // months from today's month
  const [stripSwipeDelta, setStripSwipeDelta] = useState(0); // px finger offset during drag
  const [stripSwipeAnimating, setStripSwipeAnimating] = useState(false); // true while snapping
  const [stripSwipeDir, setStripSwipeDir] = useState(null); // "left" | "right" during drag/snap
  const stripSwipeStartX = useRef(null);
  const stripSwipeContainerW = useRef(0);
  const stripSwipeDirRef = useRef(null); // mirrors stripSwipeDir for reliable handler reads
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [managerPanel, setManagerPanel] = useState("home"); // "home" | "myteam" | "timeoff"
  const [mgrTeamLoc, setMgrTeamLoc] = useState("");
  const [mgrTimeOff, setMgrTimeOff] = useState({ startDate: "", endDate: "", reason: "", error: "", submitting: false });
  const [mgrTimeOffCancel, setMgrTimeOffCancel] = useState(null);
  const [mgrDrawerMenuOpen, setMgrDrawerMenuOpen] = useState(null);
  const [cancelInviteConfirm, setCancelInviteConfirm] = useState(null);
  const [contactCard, setContactCard] = useState(null);

  function openDrawer() { setDrawerOpen(true); setTimeout(() => setDrawerVisible(true), 10); }
  function closeDrawer() { setDrawerVisible(false); setTimeout(() => { setDrawerOpen(false); setManagerPanel("home"); }, 300); }

  function handleStripScroll() {
    const el = stripScrollRef.current;
    if (!el) return;
    if (el.scrollWidth - el.scrollLeft - el.clientWidth < 300) {
      setStripDayCount(c => c + 60);
    }
    // Today tile is always the leftmost (index 0), tile width ~56px + gap 6px
    setStripTodayVisible(el.scrollLeft < 56);
  }

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes livePulse {
        0% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.2; transform: scale(1.8); }
        100% { opacity: 1; transform: scale(1); }
      }
      .live-dot {
        animation: livePulse 1s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // ── Shift form state ──────────────────────────────────────────────────────
  const [shiftFormOpen, setShiftFormOpen] = useState(false);
  const [shiftDates, setShiftDates] = useState(() => new Set([selectedDate]));
  const [shiftMultiDay, setShiftMultiDay] = useState(false);
  const [shiftRecurring, setShiftRecurring] = useState(false);
  const [shiftRecurringEnd, setShiftRecurringEnd] = useState(null);
  const [shiftRecurCalMonth, setShiftRecurCalMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const [shiftBlockedDates, setShiftBlockedDates] = useState(new Set());
  const [shiftForm, setShiftForm] = useState({
    employee_id: "",
    date: selectedDate,
    start_time: "09:00",
    end_time: "17:00",
    location: currentLocation,
  });
  // ── Availability data (on-demand per selected employee) ───────────────────
  const [selectedShiftAvailability, setSelectedShiftAvailability] = useState([]);
  useEffect(() => {
    if (!shiftFormOpen || !shiftForm.employee_id) { setSelectedShiftAvailability([]); return; }
    supabase.from("availability").select("*").eq("employee_id", shiftForm.employee_id)
      .then(({ data }) => setSelectedShiftAvailability(data || []));
  }, [shiftFormOpen, shiftForm.employee_id]);

  const [shiftSubmitting, setShiftSubmitting] = useState(false);
  const [shiftError, setShiftError] = useState("");
  const [shiftSuccess, setShiftSuccess] = useState(false);
  const [shiftConflictDates, setShiftConflictDates] = useState(new Set());
  const [shiftConflictDetails, setShiftConflictDetails] = useState([]);
  const [shiftToast, setShiftToast] = useState(null);
  const shiftToastTimer = useRef(null);
  const [eventToast, setEventToast] = useState(null);
  const eventToastTimer = useRef(null);

  function openShiftForm() {
    setShiftForm((f) => ({ ...f, date: selectedDate }));
    setShiftDates(new Set([selectedDate]));
    setShiftError("");
    setShiftSuccess(false);
    setShiftConflictDates(new Set());
    setShiftConflictDetails([]);
    setShiftFormOpen(true);
  }

  function closeShiftForm() {
    setShiftFormOpen(false);
    setShiftError("");
    setShiftSuccess(false);
    setShiftConflictDates(new Set());
    setShiftConflictDetails([]);
    setShiftDates(new Set([selectedDate]));
    setShiftMultiDay(false);
    setShiftRecurring(false);
    setShiftRecurringEnd(null);
    setShiftForm({
      employee_id: "",
      date: selectedDate,
      start_time: "09:00",
      end_time: "17:00",
      location: currentLocation,
    });
  }

  useEffect(() => {
    setShiftForm(f => ({ ...f, location: currentLocation }));
  }, [currentLocation]); // eslint-disable-line

  useEffect(() => {
    if (!shiftForm.employee_id) { setShiftBlockedDates(new Set()); return; }
    const blocked = new Set();
    for (const r of requests) {
      if (r.type !== "timeoff" || r.status !== "approved" || r.requester_id !== shiftForm.employee_id) continue;
      const start = new Date(r.date + "T00:00:00");
      const end = new Date((r.end_date || r.date) + "T00:00:00");
      const cur = new Date(start);
      while (cur <= end) {
        blocked.add(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`);
        cur.setDate(cur.getDate() + 1);
      }
    }
    setShiftBlockedDates(blocked);
    // Remove any already-selected dates that are now blocked
    setShiftDates(prev => { const s = new Set(prev); for (const d of blocked) s.delete(d); return s; });
  }, [shiftForm.employee_id, requests]); // eslint-disable-line

  async function submitShift(e) {
    e.preventDefault();
    if (!shiftForm.employee_id || shiftDates.size === 0) {
      setShiftError("Please select a staff member and date.");
      return;
    }
    if (shiftForm.start_time >= shiftForm.end_time) {
      setShiftError("End time must be after start time.");
      return;
    }

    const profile = allProfiles.find((p) => p.id === shiftForm.employee_id);
    const name = profile?.name || "Staff";
    const newShiftStart = shiftForm.start_time;
    const newShiftEnd = shiftForm.end_time;
    const selectedDates = [...shiftDates].sort();

    let cleanDates = [];
    const conflictDetails = [];

    for (const date of selectedDates) {
      const hasTimeOff = requests.some(r =>
        r.type === "timeoff" && r.status === "approved" &&
        r.requester_id === shiftForm.employee_id &&
        date >= r.date && date <= (r.end_date || r.date)
      );
      if (hasTimeOff) {
        conflictDetails.push({ date, reason: "has approved time off" });
        continue;
      }

      const overlappingShift = shifts.find(s =>
        s.employee_id === shiftForm.employee_id &&
        s.date === date &&
        newShiftStart < s.end_time &&
        newShiftEnd > s.start_time
      );
      if (overlappingShift) {
        const isSameLocation = overlappingShift.location === shiftForm.location;
        conflictDetails.push({
          date,
          reason: `already scheduled ${fmt(overlappingShift.start_time)} – ${fmt(overlappingShift.end_time)}${!isSameLocation ? ` in ${overlappingShift.location}` : ""}`,
        });
        continue;
      }

      cleanDates.push(date);
    }

    if (shiftRecurring && shiftRecurringEnd) {
      const selectedWeekdays = new Set([...shiftDates].map(d => new Date(d + "T00:00:00").getDay()));
      const endDate = new Date(shiftRecurringEnd + "T00:00:00");
      const allDates = new Set(cleanDates);
      const earliest = new Date(Math.min(...cleanDates.map(d => new Date(d + "T00:00:00").getTime())));
      const cur = new Date(earliest);
      cur.setDate(cur.getDate() + 1);
      while (cur <= endDate) {
        if (selectedWeekdays.has(cur.getDay())) {
          allDates.add(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`);
        }
        cur.setDate(cur.getDate() + 1);
      }
      cleanDates = [...allDates];
    }

    // All conflict — show inline errors, keep sheet open
    if (cleanDates.length === 0) {
      setShiftConflictDates(new Set(conflictDetails.map(c => c.date)));
      setShiftConflictDetails(conflictDetails);
      setShiftError("");
      return;
    }

    // Some or all clean — submit only clean dates
    setShiftSubmitting(true);
    setShiftError("");
    setShiftConflictDates(new Set());
    setShiftConflictDetails([]);

    const rows = cleanDates.map(date => ({
      employee_id: shiftForm.employee_id,
      employee_name: profile?.name || "",
      date,
      start_time: shiftForm.start_time,
      end_time: shiftForm.end_time,
      location: shiftForm.location,
      scheduled_by: user?.name || null,
      org_id: user?.org_id || null,
    }));

    const { error } = await supabase.from("shifts").insert(rows);
    setShiftSubmitting(false);

    if (error) {
      setShiftError(error.message || "Something went wrong. Please try again.");
      return;
    }

    const timeRange = `${fmt(shiftForm.start_time)} – ${fmt(shiftForm.end_time)}`;
    const successLine = `Shift added for ${name} — ${cleanDates.map(fmtReqDate).join(", ")} ${timeRange}`;
    const conflictLines = conflictDetails.map(c =>
      `Couldn't be added on ${fmtReqDate(c.date)} — ${name} ${c.reason}.`
    );

    if (shiftToastTimer.current) clearTimeout(shiftToastTimer.current);
    setShiftToast({ successLine, conflictLines });
    shiftToastTimer.current = setTimeout(() => setShiftToast(null), 5000);

    onShiftAdded?.();
    closeShiftForm();
  }

  // ── Event form state ──────────────────────────────────────────────────────
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [evType, setEvType] = useState("");
  const [addingNewType, setAddingNewType] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [evTitle, setEvTitle] = useState("");
  const [evDates, setEvDates] = useState(new Set());
  const [eventMultiDay, setEventMultiDay] = useState(false);
  const [evStartTime, setEvStartTime] = useState("09:00");
  const [evEndTime, setEvEndTime] = useState("10:00");
  const [evSpot, setEvSpot] = useState("");
  const [evNotes, setEvNotes] = useState("");
  const [evRecurring, setEvRecurring] = useState(false);
  const [evRecurringEnd, setEvRecurringEnd] = useState(null);
  const [evRecurCalMonth, setEvRecurCalMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const [evLocation, setEvLocation] = useState(currentLocation);
  const [eventSubmitting, setEventSubmitting] = useState(false);
  const [eventError, setEventError] = useState("");

  useEffect(() => {
    setEvLocation(currentLocation);
  }, [currentLocation]); // eslint-disable-line

  function openEventForm() {
    setEvType("");
    setEvTitle("");
    setEvDates(new Set([selectedDate]));
    setEvStartTime("09:00");
    setEvEndTime("10:00");
    setEvNotes("");
    setEvLocation(currentLocation);
    setEvRecurring(false);
    setEvRecurringEnd(null);
    setEventError("");
    setEventFormOpen(true);
    supabase.from("event_types").select("name").eq("org_id", user?.org_id).order("name").then(({ data }) => {
      if (data) setEventTypes(data.map(r => r.name));
    });
  }

  function closeEventForm() {
    setEventFormOpen(false);
    setEventError("");
    setEventMultiDay(false);
    setAddingNewType(false);
    setNewTypeName("");
  }

  async function submitNewType(onSelect) {
    const trimmed = newTypeName.trim();
    if (!trimmed) { setAddingNewType(false); setNewTypeName(""); return; }
    if (eventTypes.some(t => t.toLowerCase() === trimmed.toLowerCase())) {
      setAddingNewType(false); setNewTypeName(""); return;
    }
    const { error } = await supabase.from("event_types").insert({ name: trimmed, org_id: user?.org_id, active: true });
    if (error) { setAddingNewType(false); setNewTypeName(""); return; }
    const { data } = await supabase.from("event_types").select("name").eq("org_id", user?.org_id).order("name");
    if (data) setEventTypes(data.map(r => r.name));
    if (onSelect) onSelect(trimmed); else setEvType(trimmed);
    setAddingNewType(false);
    setNewTypeName("");
  }

  function toggleEvDate(dateStr) {
    setEvDates(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) { next.delete(dateStr); } else { next.add(dateStr); }
      return next;
    });
  }

  function addHour(hhmm) {
    const [h, m] = hhmm.split(":").map(Number);
    const total = h * 60 + m + 60;
    return `${String(Math.min(Math.floor(total / 60), 23)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  }

  async function submitEvent() {
    if (!evTitle.trim() || evDates.size === 0 || !evStartTime || !evEndTime) {
      setEventError("Please fill in title, date, and times.");
      return;
    }
    if (evStartTime >= evEndTime) {
      setEventError("End time must be after start time.");
      return;
    }
    setEventSubmitting(true);
    setEventError("");

    let datesToInsert = [...evDates];
    if (evRecurring && evRecurringEnd) {
      const selectedWeekdays = new Set([...evDates].map(d => new Date(d + "T00:00:00").getDay()));
      const endDate = new Date(evRecurringEnd + "T00:00:00");
      const allDates = new Set(datesToInsert);
      const earliest = new Date(Math.min(...datesToInsert.map(d => new Date(d + "T00:00:00").getTime())));
      const cur = new Date(earliest);
      cur.setDate(cur.getDate() + 1);
      while (cur <= endDate) {
        if (selectedWeekdays.has(cur.getDay())) {
          allDates.add(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`);
        }
        cur.setDate(cur.getDate() + 1);
      }
      datesToInsert = [...allDates];
    }

    const rows = datesToInsert.map(date => ({
      title: evTitle.trim(),
      type: evType || "Event",
      date,
      start_time: evStartTime,
      end_time: evEndTime,
      location: evLocation,
      notes: evNotes.trim() || null,
      created_by: user?.name || null,
      org_id: user?.org_id || null,
    }));

    const { error } = await supabase.from("events").insert(rows);
    setEventSubmitting(false);
    if (error) {
      setEventError(error.message || "Something went wrong. Please try again.");
    } else {
      const firstDate = [...evDates].sort()[0];
      const msg = `Event added — ${fmtReqDate(firstDate)} at ${fmt(evStartTime)}`;
      if (eventToastTimer.current) clearTimeout(eventToastTimer.current);
      setEventToast(msg);
      eventToastTimer.current = setTimeout(() => setEventToast(null), 4000);
      onShiftAdded?.();
      closeEventForm();
    }
  }

  // ── Delete / cancel state ─────────────────────────────────────────────────
  const [deleteShiftTarget, setDeleteShiftTarget] = useState(null);
  const [deletingShift, setDeletingShift] = useState(false);
  const [editShiftTarget, setEditShiftTarget] = useState(null);
  const [editShiftTimes, setEditShiftTimes] = useState({ start_time: "09:00", end_time: "17:00" });
  const [editShiftVisible, setEditShiftVisible] = useState(false);
  const [editShiftSubmitting, setEditShiftSubmitting] = useState(false);
  const [editShiftError, setEditShiftError] = useState("");
  const [editShiftConflict, setEditShiftConflict] = useState(null); // { event, name }
  const [editEventTarget, setEditEventTarget] = useState(null);
  const [editEventForm, setEditEventForm] = useState({ title: "", type: "", start_time: "09:00", end_time: "10:00", notes: "", date: "" });
  const [editEventVisible, setEditEventVisible] = useState(false);
  const [editEventSubmitting, setEditEventSubmitting] = useState(false);
  const [editEventError, setEditEventError] = useState("");

  function openEditEvent(ev) {
    setEditEventForm({
      title: ev.title || "",
      type: ev.type || "",
      start_time: ev.start_time || "09:00",
      end_time: ev.end_time || "10:00",
      notes: ev.notes || "",
      date: ev.date || "",
    });
    setEditEventError("");
    setEditEventSubmitting(false);
    setAddingNewType(false);
    setNewTypeName("");
    setEditEventTarget(ev);
    setTimeout(() => setEditEventVisible(true), 10);
    if (eventTypes.length === 0) {
      supabase.from("event_types").select("name").eq("org_id", user?.org_id).order("name").then(({ data }) => {
        if (data) setEventTypes(data.map(r => r.name));
      });
    }
  }

  function closeEditEvent() {
    setEditEventVisible(false);
    setTimeout(() => setEditEventTarget(null), 300);
  }

  async function submitEditEvent() {
    if (!editEventForm.title.trim()) { setEditEventError("Title is required."); return; }
    if (!editEventForm.type) { setEditEventError("Type is required."); return; }
    if (editEventForm.start_time >= editEventForm.end_time) { setEditEventError("End time must be after start time."); return; }
    setEditEventSubmitting(true);
    setEditEventError("");
    const dateChanged = editEventForm.date && editEventForm.date !== editEventTarget.date;
    if (dateChanged) {
      await supabase.from("event_staff").delete().eq("event_id", editEventTarget.id);
    }
    const { error } = await supabase
      .from("events")
      .update({
        title: editEventForm.title.trim(),
        type: editEventForm.type || "Event",
        start_time: editEventForm.start_time,
        end_time: editEventForm.end_time,
        notes: editEventForm.notes.trim() || null,
        date: editEventForm.date || editEventTarget.date,
      })
      .eq("id", editEventTarget.id);
    setEditEventSubmitting(false);
    if (error) {
      setEditEventError(error.message || "Something went wrong.");
    } else {
      // Update assignEventTarget so the assign sheet reflects the new values immediately
      setAssignEventTarget(prev => prev && prev.id === editEventTarget.id
        ? { ...prev, title: editEventForm.title.trim(), type: editEventForm.type || "Event", start_time: editEventForm.start_time, end_time: editEventForm.end_time, date: editEventForm.date || editEventTarget.date }
        : prev
      );
      onShiftAdded?.();
      closeEditEvent();
    }
  }

  function openEditShift(shift) {
    setEditShiftTarget(shift);
    setEditShiftTimes({ start_time: shift.start_time || "09:00", end_time: shift.end_time || "17:00" });
    setEditShiftError("");
    setEditShiftSubmitting(false);
    setTimeout(() => setEditShiftVisible(true), 10);
  }

  function closeEditShift() {
    setEditShiftVisible(false);
    setTimeout(() => setEditShiftTarget(null), 300);
  }

  async function doSaveShiftTimes() {
    setEditShiftSubmitting(true);
    setEditShiftError("");
    const { error } = await supabase
      .from("shifts")
      .update({ start_time: editShiftTimes.start_time, end_time: editShiftTimes.end_time })
      .eq("id", editShiftTarget.id);
    setEditShiftSubmitting(false);
    if (error) {
      setEditShiftError(error.message || "Something went wrong.");
    } else {
      onShiftAdded?.();
      closeEditShift();
    }
  }

  async function submitEditShift() {
    if (!editShiftTarget || !editShiftTarget.employee_id || !editShiftTarget.date) {
      console.warn("submitEditShift: missing required shift fields, aborting");
      return;
    }
    if (editShiftTimes.start_time >= editShiftTimes.end_time) {
      setEditShiftError("End time must be after start time.");
      return;
    }

    // Check if the employee is assigned to any events on this date that fall
    // outside (or aren't fully covered by) the new shift window.
    const newStart = editShiftTimes.start_time;
    const newEnd = editShiftTimes.end_time;
    const empId = editShiftTarget.employee_id;
    const shiftDate = editShiftTarget.date;

    const employeeEventIds = new Set(
      eventStaff.filter(es => es.employee_id === empId).map(es => es.event_id)
    );
    const conflictingEvent = events.find(ev =>
      ev.date === shiftDate &&
      employeeEventIds.has(ev.id) &&
      ev.start_time && ev.end_time &&
      (ev.start_time < newStart || ev.end_time > newEnd)
    );

    if (conflictingEvent) {
      const profile = allProfiles.find(p => p.id === empId);
      const name = profile
        ? (profile.name || `${profile.first_name || ""} ${profile.last_name || ""}`.trim())
        : editShiftTarget.employee_name || "Employee";
      setEditShiftConflict({ event: conflictingEvent, name });
      return;
    }

    await doSaveShiftTimes();
  }

  async function doRemoveAndSave() {
    const { event } = editShiftConflict;
    setEditShiftConflict(null);
    // Remove employee from the conflicting event, then save shift times
    await supabase
      .from("event_staff")
      .delete()
      .eq("event_id", event.id)
      .eq("employee_id", editShiftTarget.employee_id);
    await doSaveShiftTimes();
  }
  const [cancelEventTarget, setCancelEventTarget] = useState(null);
  const [cancellingEvent, setCancellingEvent] = useState(false);

  async function confirmDeleteShift() {
    setDeletingShift(true);
    const id = deleteShiftTarget.id;
    await supabase.from("requests").delete().eq("shift_id", id);
    await supabase.from("requests").delete().eq("cover_shift_id", id);
    await supabase.from("shift_swap_requests").delete().eq("requester_shift_id", id);
    await supabase.from("shift_swap_requests").delete().eq("target_shift_id", id);
    const eventsOnDate = events.filter(e => e.date === deleteShiftTarget.date);
    const assignedEventIds = eventsOnDate
      .filter(e => eventStaff.some(es => es.event_id === e.id && es.employee_id === deleteShiftTarget.employee_id))
      .map(e => e.id);
    if (assignedEventIds.length > 0) {
      await supabase.from("event_staff").delete()
        .eq("employee_id", deleteShiftTarget.employee_id)
        .in("event_id", assignedEventIds);
    }
    await supabase.from("shifts").delete().eq("id", id);
    setDeletingShift(false);
    setDeleteShiftTarget(null);
    onShiftAdded?.();
  }

  async function confirmCancelEvent() {
    setCancellingEvent(true);
    await supabase.from("events").delete().eq("id", cancelEventTarget.id);
    setCancellingEvent(false);
    setCancelEventTarget(null);
    onShiftAdded?.();
  }

  // ── Assign staff state ────────────────────────────────────────────────────
  const [assignEventTarget, setAssignEventTarget] = useState(null);

  async function assignStaff(profile) {
    await supabase.from("event_staff").insert({
      event_id: assignEventTarget.id,
      employee_id: profile.id,
      employee_name: profile.name || "",
      org_id: user?.org_id || null,
    });
    onShiftAdded?.();
  }

  async function removeStaff(profile) {
    await supabase
      .from("event_staff")
      .delete()
      .eq("event_id", assignEventTarget.id)
      .eq("employee_id", profile.id);
    onShiftAdded?.();
  }

  const employeeBelongsToLocation = (employeeId) => {
    const p = allProfiles.find(pr => pr.id === employeeId);
    if (!p) return false;
    if (p.locations && p.locations.length > 0) return p.locations.includes(currentLocation);
    return (p.location || currentLocation) === currentLocation;
  };

  const actionableRequests = requests.filter(r =>
    (r.type === "timeoff" && r.status === "pending" && employeeBelongsToLocation(r.requester_id)) ||
    (r.type === "swap" && r.status === "pending_manager") ||
    (r.type === "cover" && r.status === "pending" && (r.shift_location || currentLocation) === currentLocation)
  );

  const approvedTimeOffRequests = requests.filter(r => {
    if (r.type !== "timeoff" || r.status !== "approved") return false;
    if (!employeeBelongsToLocation(r.requester_id)) return false;
    const today = new Date().toISOString().split("T")[0];
    const endDate = r.end_date || r.date;
    return endDate >= today;
  });

  const [requestConfirm, setRequestConfirm] = useState(null);
  const [coverAssignSheet, setCoverAssignSheet] = useState(null);
  const [coverAvailability, setCoverAvailability] = useState([]);
  useEffect(() => {
    if (!coverAssignSheet?.selectedEmployeeId) { setCoverAvailability([]); return; }
    supabase.from("availability").select("*").eq("employee_id", coverAssignSheet.selectedEmployeeId)
      .then(({ data }) => setCoverAvailability(data || []));
  }, [coverAssignSheet?.selectedEmployeeId]);
  const [showApproved, setShowApproved] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [eventSheetVisible, setEventSheetVisible] = useState(false);
  useEffect(() => {
    if (eventFormOpen) { setTimeout(() => setEventSheetVisible(true), 10); }
    else { setEventSheetVisible(false); }
  }, [eventFormOpen]);

  const [shiftSheetVisible, setShiftSheetVisible] = useState(false);
  useEffect(() => {
    if (shiftFormOpen) { setTimeout(() => setShiftSheetVisible(true), 10); }
    else { setShiftSheetVisible(false); }
  }, [shiftFormOpen]);

  // ── Date strip ────────────────────────────────────────────────────────────
  const days = buildDays(events, eventStaff, allProfiles, stripDayCount, currentLocation);
  const statusMap = {};
  for (const d of days) statusMap[d.date] = d.status;
  const todayStr = getToday();
  const stripDates = Array.from({ length: stripDayCount }, (_, i) => {
    const d = new Date(todayStr + "T00:00:00");
    d.setDate(d.getDate() + i);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { dateStr, dayLabel: DAY_LABELS[d.getDay()], dayNum: d.getDate(), monthLabel: MONTH_SHORT[d.getMonth()], year: d.getFullYear() };
  });

  // ── Selected day data ─────────────────────────────────────────────────────
  const dayEvents = events
    .filter((e) => e.date === selectedDate && (!e.location || e.location === currentLocation))
    .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));

  const dayHeadline = formatDayHeadline(selectedDate);
  const isTodayOrTomorrow = dayHeadline === "Today" || dayHeadline === "Tomorrow";
  const isToday = selectedDate === getToday();
  const activeEvents = isToday
    ? dayEvents.filter((ev) => !(ev.end_time && nowHHMM >= ev.end_time))
    : dayEvents;
  const statusPill = getDayStatusPill(activeEvents, eventStaff, allProfiles);

  const dayShifts = shifts
    .filter((s) => s.date === selectedDate && s.location === currentLocation)
    .sort((a, b) => {
      const pa = allProfiles.find(p => p.id === a.employee_id);
      const pb = allProfiles.find(p => p.id === b.employee_id);
      const rank = (p) => p?.role === "manager" ? 0 : isHead(p) ? 1 : 2;
      const ra = rank(pa), rb = rank(pb);
      if (ra !== rb) return ra - rb;
      return (pb?.cit_stars || 0) - (pa?.cit_stars || 0);
    });

  const employeeProfiles = allProfiles.filter((p) => {
    const roleOk = p.role?.toLowerCase() === "employee" || p.role?.toLowerCase() === "manager";
    if (!roleOk) return false;
    // Exclude admins unless it's the current admin viewing (self-assign only)
    const isSelfAdmin = p.is_admin === true && p.id === user?.id && user?.is_admin === true;
    if (p.is_admin === true && !isSelfAdmin) return false;
    if (p.removed === true || p.active === false) return false;
    // Admin self-assign bypasses location filter (admin can work any location)
    if (isSelfAdmin) return true;
    if (p.locations && p.locations.length > 0) return p.locations.includes(currentLocation);
    return (p.location || currentLocation) === currentLocation;
  });

  return (
    <div className="bg-white font-sans md:min-h-screen md:flex md:flex-col" style={{ overflow: "visible", paddingTop: "env(safe-area-inset-top, 20px)" }}>
      {/* ── Mobile header (hidden on desktop) ── */}
      <div className="md:hidden px-5" style={{ paddingTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 900, letterSpacing: "-0.5px", color: "#0A0A0A", margin: 0, lineHeight: 1, fontFamily: "'Oswald', sans-serif" }}>
              TMRW<span style={{ color: "#F55C00" }}>.</span>
            </h1>
          </div>
          <select
            value={currentLocation}
            onChange={(e) => onLocationChange?.(e.target.value)}
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: "uppercase",
              background: "transparent",
              border: "none",
              color: "#F55C00",
              cursor: "pointer",
              outline: "none",
              appearance: "none",
              WebkitAppearance: "none",
              padding: "4px 20px 4px 4px",
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23F55C00' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 2px center",
            }}
          >
            {(user?.is_admin === true
              ? allLocations
              : allLocations.filter(l => user?.locations?.includes(l.name))
            ).map(l => (
              <option key={l.name} value={l.name}>{l.name.toUpperCase()}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={openDrawer}
            aria-label="Menu"
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              display: "flex", flexDirection: "column", justifyContent: "space-between",
              alignItems: "stretch", width: 44, height: 44, padding: "13px 11px",
              overflow: "visible", WebkitTapHighlightColor: "transparent",
            }}
          >
            <span style={{ display: "block", height: 2, background: "#111", borderRadius: 1 }} />
            <span style={{ display: "block", height: 2, background: "#111", borderRadius: 1 }} />
            <span style={{ display: "block", height: 2, background: "#111", borderRadius: 1 }} />
          </button>
        </div>
      </div>

      {/* ── Desktop header row 1: logo | location switcher | hamburger (hidden on mobile) ── */}
      <div className="hidden md:grid md:flex-none" style={{ gridTemplateColumns: "1fr auto 1fr", alignItems: "center", padding: "16px 32px 10px", borderBottom: "1px solid #EFEFEF" }}>
        {/* Left: logo */}
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 900, letterSpacing: "-0.5px", color: "#0A0A0A", margin: 0, lineHeight: 1, fontFamily: "'Oswald', sans-serif" }}>
            TMRW<span style={{ color: "#F55C00" }}>.</span>
          </h1>
        </div>
        {/* Center: location switcher */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <select
            value={currentLocation}
            onChange={(e) => onLocationChange?.(e.target.value)}
            style={{
              fontFamily: "'Oswald', sans-serif",
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: "uppercase",
              background: "transparent",
              border: "none",
              color: "#F55C00",
              cursor: "pointer",
              outline: "none",
              appearance: "none",
              WebkitAppearance: "none",
              padding: "4px 24px 4px 4px",
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23F55C00' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 2px center",
            }}
          >
            {(user?.is_admin === true
              ? allLocations
              : allLocations.filter(l => user?.locations?.includes(l.name))
            ).map(l => (
              <option key={l.name} value={l.name}>{l.name.toUpperCase()}</option>
            ))}
          </select>
        </div>
        {/* Right: hamburger */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={openDrawer}
            aria-label="Menu"
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              display: "flex", flexDirection: "column", justifyContent: "space-between",
              alignItems: "stretch", width: 44, height: 44, padding: "13px 11px",
              overflow: "visible", WebkitTapHighlightColor: "transparent",
            }}
          >
            <span style={{ display: "block", height: 2, background: "#111", borderRadius: 1 }} />
            <span style={{ display: "block", height: 2, background: "#111", borderRadius: 1 }} />
            <span style={{ display: "block", height: 2, background: "#111", borderRadius: 1 }} />
          </button>
        </div>
      </div>

      {/* ── Desktop header row 2: Back to Today + toggle (left) | Add Event + Add Shift (right) ── */}
      <div className="hidden md:flex md:flex-none items-center justify-between" style={{ padding: "10px 32px", borderBottom: "1px solid #EFEFEF" }}>
        {/* Left: Back to Today + calendar toggle */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {(stripCalMode === "week" ? (!stripTodayVisible || selectedDate !== todayStr) : stripMonthOffset > 0) && (
            <button
              type="button"
              onClick={() => {
                setSelectedDate(todayStr);
                if (stripCalMode === "week") {
                  if (stripScrollRef.current) stripScrollRef.current.scrollLeft = 0;
                  setStripTodayVisible(true);
                } else {
                  setStripMonthOffset(0);
                }
              }}
              style={{ fontSize: 11, fontWeight: 600, color: "#555", background: "white", border: "1px solid #DDD", borderRadius: 20, padding: "4px 12px", cursor: "pointer", fontFamily: "inherit", WebkitTapHighlightColor: "transparent" }}
            >← Back to Today</button>
          )}
          <button
            type="button"
            onClick={() => {
              const next = stripCalMode === "week" ? "month" : "week";
              setStripCalMode(next);
              if (next === "month") {
                const sel = new Date(selectedDate + "T00:00:00");
                const today = new Date(todayStr + "T00:00:00");
                const diffMonths = (sel.getFullYear() - today.getFullYear()) * 12 + (sel.getMonth() - today.getMonth());
                setStripMonthOffset(Math.max(0, diffMonths));
              } else {
                setTimeout(() => {
                  const el = stripScrollRef.current;
                  if (!el) return;
                  const selD = new Date(selectedDate + "T00:00:00");
                  const todD = new Date(todayStr + "T00:00:00");
                  const dayDiff = Math.round((selD - todD) / 86400000);
                  const tileW = 62;
                  el.scrollLeft = Math.max(0, dayDiff * tileW);
                  setStripTodayVisible(el.scrollLeft < 56);
                }, 0);
              }
            }}
            aria-label={stripCalMode === "week" ? "Switch to month view" : "Switch to week view"}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "6px 8px", color: "#555", display: "flex", alignItems: "center", justifyContent: "center", WebkitTapHighlightColor: "transparent" }}
          >
            {stripCalMode === "week" ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor"/>
                  <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor"/>
                  <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor"/>
                  <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor"/>
                </svg>
                <span style={{ fontSize: 9, fontWeight: 900, color: "#F55C00", letterSpacing: 1, fontFamily: "inherit" }}>M</span>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect x="1" y="4" width="6" height="8" rx="1.5" fill="currentColor"/>
                  <rect x="9" y="4" width="6" height="8" rx="1.5" fill="currentColor"/>
                </svg>
                <span style={{ fontSize: 9, fontWeight: 900, color: "#F55C00", letterSpacing: 1, fontFamily: "inherit" }}>W</span>
              </div>
            )}
          </button>
        </div>
        {/* Right: Add Event + Add Shift */}
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button type="button" onClick={openEventForm} style={{ background: "#F55C00", border: "none", borderRadius: 8, padding: "8px 18px", color: "white", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>+ Add Event</button>
          <button type="button" onClick={openShiftForm} style={{ background: "#111", border: "none", borderRadius: 8, padding: "8px 18px", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>+ Add Shift</button>
        </div>
      </div>

      {/* ── Date strip + day label (spans both columns on desktop) ── */}
      <div className="px-5 md:flex-none" style={{ overflow: "visible", borderBottom: "1px solid #EFEFEF" }}>

        {/* ── Date Strip ── */}
        <div style={{ paddingTop: 6, marginBottom: 20, overflow: "visible" }}>
          {/* Header row: Back to Today + mode toggle — mobile only (desktop has these in the top bar) */}
          <div className="md:hidden">
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 4, minHeight: 44 }}>
            {(stripCalMode === "week" ? (!stripTodayVisible || selectedDate !== todayStr) : stripMonthOffset > 0) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedDate(todayStr);
                  if (stripCalMode === "week") {
                    if (stripScrollRef.current) stripScrollRef.current.scrollLeft = 0;
                    setStripTodayVisible(true);
                  } else {
                    setStripMonthOffset(0);
                  }
                }}
                style={{ fontSize: 11, fontWeight: 600, color: "#555", background: "white", border: "1px solid #DDD", borderRadius: 20, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", WebkitTapHighlightColor: "transparent", flexShrink: 0 }}
              >← Back to Today</button>
            )}
            {/* Mode toggle icon */}
            <button
              type="button"
              onClick={() => {
                const next = stripCalMode === "week" ? "month" : "week";
                setStripCalMode(next);
                if (next === "month") {
                  // Sync month view to the currently selected date's month
                  const sel = new Date(selectedDate + "T00:00:00");
                  const today = new Date(todayStr + "T00:00:00");
                  const diffMonths = (sel.getFullYear() - today.getFullYear()) * 12 + (sel.getMonth() - today.getMonth());
                  setStripMonthOffset(Math.max(0, diffMonths));
                } else {
                  // Snap week strip to selected date
                  setTimeout(() => {
                    const el = stripScrollRef.current;
                    if (!el) return;
                    const selD = new Date(selectedDate + "T00:00:00");
                    const todD = new Date(todayStr + "T00:00:00");
                    const dayDiff = Math.round((selD - todD) / 86400000);
                    const tileW = 62; // 56px tile + 6px gap
                    el.scrollLeft = Math.max(0, dayDiff * tileW);
                    setStripTodayVisible(el.scrollLeft < 56);
                  }, 0);
                }
              }}
              aria-label={stripCalMode === "week" ? "Switch to month view" : "Switch to week view"}
              style={{ background: "none", border: "none", cursor: "pointer", minWidth: 44, minHeight: 44, padding: "0 8px", color: "#555", display: "flex", alignItems: "center", justifyContent: "center", WebkitTapHighlightColor: "transparent", flexShrink: 0 }}
            >
              {stripCalMode === "week" ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor"/>
                    <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor"/>
                    <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor"/>
                    <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor"/>
                  </svg>
                  <span style={{ fontSize: 9, fontWeight: 900, color: "#F55C00", letterSpacing: 1, fontFamily: "inherit" }}>M</span>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="1" y="4" width="6" height="8" rx="1.5" fill="currentColor"/>
                    <rect x="9" y="4" width="6" height="8" rx="1.5" fill="currentColor"/>
                  </svg>
                  <span style={{ fontSize: 9, fontWeight: 900, color: "#F55C00", letterSpacing: 1, fontFamily: "inherit" }}>W</span>
                </div>
              )}
            </button>
          </div>
          </div>{/* end md:hidden wrapper for Back to Today + toggle */}

          <style>{`
            .strip-scroll::-webkit-scrollbar { display: none; }
            .strip-scroll { scrollbar-width: none; }
            @media (min-width: 768px) {
              .strip-scroll::-webkit-scrollbar { display: block; height: 4px; }
              .strip-scroll::-webkit-scrollbar-track { background: #f0f0f0; border-radius: 2px; }
              .strip-scroll::-webkit-scrollbar-thumb { background: #ccc; border-radius: 2px; }
              .strip-scroll::-webkit-scrollbar-thumb:hover { background: #F55C00; }
              .strip-scroll { scrollbar-width: thin; scrollbar-color: #ccc #f0f0f0; cursor: grab; }
              .strip-scroll:active { cursor: grabbing; }
              .strip-scroll { transform: scaleY(-1); }
              .strip-scroll > * { transform: scaleY(-1); }
            }
          `}</style>

          {/* ── WEEK MODE ── */}
          {stripCalMode === "week" && (
            <>
            <div
              ref={stripScrollRef}
              className="strip-scroll"
              onScroll={handleStripScroll}
              onMouseDown={e => { stripIsDragging.current = true; stripDragMoved.current = false; stripDragStartX.current = e.pageX - stripScrollRef.current.offsetLeft; stripDragScrollLeft.current = stripScrollRef.current.scrollLeft; stripScrollRef.current.style.cursor = "grabbing"; }}
              onMouseMove={e => { if (!stripIsDragging.current) return; const x = e.pageX - stripScrollRef.current.offsetLeft; const walk = x - stripDragStartX.current; if (Math.abs(walk) > 4) stripDragMoved.current = true; stripScrollRef.current.scrollLeft = stripDragScrollLeft.current - walk; }}
              onMouseUp={() => { stripIsDragging.current = false; stripScrollRef.current.style.cursor = "grab"; }}
              onMouseLeave={() => { stripIsDragging.current = false; if (stripScrollRef.current) stripScrollRef.current.style.cursor = "grab"; }}
              style={{ display: "flex", gap: 4, overflowX: "auto", overflowY: "visible", paddingBottom: 4, cursor: "grab", userSelect: "none" }}
            >
              {stripDates.map(({ dateStr, dayLabel, dayNum, monthLabel, year }) => {
                const status = statusMap[dateStr] || "covered";
                const isTodayTile = dateStr === todayStr;
                const isSelected = dateStr === selectedDate;
                const isGap = status === "has-gap" || status === "today-gap";
                const isWarn = status === "has-warn" || status === "today-warn";
                const isCovered = status === "covered" || status === "today-covered";

                let bg = "#f4f4f4";
                let numColor = "#0A0A0A";
                let labelColor = "#0A0A0A";
                let border = "none";
                if (isSelected) {
                  bg = "#F55C00"; numColor = "white"; labelColor = "rgba(255,255,255,0.8)"; border = "none";
                } else if (isTodayTile) {
                  bg = "#0A0A0A"; numColor = "white"; labelColor = "rgba(255,255,255,0.6)";
                } else if (isGap) {
                  bg = "#FFEBEA"; numColor = "#E53935"; labelColor = "rgba(229,57,53,0.7)";
                } else if (isWarn) {
                  bg = "#FFF4E0"; numColor = "#F5A623"; labelColor = "rgba(245,166,35,0.7)";
                }

                let dotColor = null;
                if (!isSelected) {
                  if (isGap) dotColor = "#E53935";
                  else if (isWarn) dotColor = "#F5A623";
                  else if (isCovered && status !== "empty" && status !== "today-empty") dotColor = "#2E7D32";
                }

                // Cell width: exactly 1/6 of available container (viewport minus px-5 padding and 5 gaps)
                const cellW = "calc((100vw - 40px - 5*4px) / 6)";
                return (
                  <div key={dateStr} style={{ flexShrink: 0, width: cellW, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    {/* Month label row — always 16px tall, text only on 1st of month */}
                    <div style={{ height: 16, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 2, overflow: "hidden" }}>
                      {dayNum === 1 && (
                        <span style={{ fontSize: 9, fontWeight: 600, color: "#111", textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap" }}>
                          {monthLabel} {year}
                        </span>
                      )}
                    </div>
                    {/* Tile — exactly cellW wide, 48px tall (above 44px tap target minimum) */}
                    <button
                      type="button"
                      onClick={() => { if (!stripDragMoved.current) setSelectedDate(dateStr); }}
                      style={{
                        position: "relative",
                        width: "100%",
                        height: 48,
                        borderRadius: 8,
                        background: bg, border: dotColor ? `1.5px solid ${dotColor}` : "none", flexShrink: 0,
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        cursor: "pointer", fontFamily: "inherit", WebkitTapHighlightColor: "transparent",
                      }}
                    >
                      <span style={{ fontSize: 8, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: labelColor }}>
                        {dayLabel}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 800, color: numColor, lineHeight: 1, marginTop: 2 }}>
                        {dayNum}
                      </span>
                      {dotColor && (
                        <span style={{ position: "absolute", bottom: 5, width: 4, height: 4, borderRadius: "50%", background: dotColor }} />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
            </>
          )}

          {/* ── MONTH MODE ── */}
          {stripCalMode === "month" && (() => {
            const MONTH_FULL = ["January","February","March","April","May","June","July","August","September","October","November","December"];
            const COL_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
            const todayD = new Date(todayStr + "T00:00:00");

            function buildCells(offset) {
              const vD = new Date(todayD.getFullYear(), todayD.getMonth() + offset, 1);
              const vYear = vD.getFullYear();
              const vMonth = vD.getMonth();
              const daysInMonth = new Date(vYear, vMonth + 1, 0).getDate();
              const firstDow = new Date(vYear, vMonth, 1).getDay();
              const cells = [];
              for (let i = 0; i < firstDow; i++) cells.push(null);
              for (let d = 1; d <= daysInMonth; d++) {
                cells.push(`${vYear}-${String(vMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
              }
              return { cells, vYear, vMonth };
            }

            function renderGrid(cells, interactive) {
              // Circle scales with cell width. Each cell = 100vw / 7.
              // 62% of cell width gives an Apple-Calendar-proportioned circle.
              const circleSize = "36px";
              const fontSize = "13px";

              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, width: "100%" }}>
                  {cells.map((dateStr, idx) => {
                    if (!dateStr) {
                      return <div key={`pad-${idx}`} />;
                    }
                    const status = statusMap[dateStr] || "covered";
                    const isTodayTile = dateStr === todayStr;
                    const isSelected = interactive && dateStr === selectedDate;
                    const isBeforeToday = dateStr < todayStr;
                    const isGap = status === "has-gap" || status === "today-gap";
                    const isWarn = status === "has-warn" || status === "today-warn";
                    const isCovered = status === "covered" || status === "today-covered";

                    const circleColor = isSelected ? "#F55C00" : isTodayTile ? "#0A0A0A" : "transparent";

                    let numColor;
                    if (isSelected || isTodayTile) numColor = "white";
                    else if (isBeforeToday) numColor = "#C0C0C0";
                    else if (isGap) numColor = "#E53935";
                    else if (isWarn) numColor = "#F5A623";
                    else numColor = "#111";

                    let dotColor = null;
                    if (!isSelected && !isBeforeToday) {
                      if (isGap) dotColor = "#E53935";
                      else if (isWarn) dotColor = "#F5A623";
                      else if (isCovered && status !== "empty" && status !== "today-empty") dotColor = "#2E7D32";
                    }

                    return (
                      <button
                        key={dateStr}
                        type="button"
                        onClick={() => { if (interactive && !isBeforeToday) setSelectedDate(dateStr); }}
                        style={{
                          height: 44, width: "100%",
                          background: "none", border: "none", padding: 0,
                          display: "flex", flexDirection: "column",
                          alignItems: "center", justifyContent: "center",
                          cursor: isBeforeToday || !interactive ? "default" : "pointer",
                          fontFamily: "inherit", WebkitTapHighlightColor: "transparent",
                        }}
                      >
                        {/* Filled circle + number */}
                        <div style={{
                          width: circleSize, height: circleSize, borderRadius: "50%",
                          background: circleColor,
                          border: (isTodayTile && dotColor) ? `1.5px solid ${dotColor}` : "none",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}>
                          <span style={{
                            fontSize,
                            fontWeight: (isSelected || isTodayTile) ? 700 : isBeforeToday ? 400 : 500,
                            color: numColor,
                            lineHeight: 1,
                          }}>
                            {new Date(dateStr + "T00:00:00").getDate()}
                          </span>
                        </div>
                        {/* Dot below circle — reserved height keeps all rows uniform */}
                        {dotColor ? (
                          <span style={{ width: 4, height: 4, borderRadius: "50%", background: dotColor, marginTop: 3, display: "block", flexShrink: 0 }} />
                        ) : (
                          <span style={{ height: 7, display: "block", flexShrink: 0 }} />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            }

            const { cells: curCells, vYear: curYear, vMonth: curMonth } = buildCells(stripMonthOffset);
            const isSwipeActive = stripSwipeDir !== null;
            const adjOffset = stripSwipeDir === "left" ? stripMonthOffset + 1 : stripMonthOffset - 1;
            const { cells: adjCells } = isSwipeActive ? buildCells(adjOffset) : { cells: [] };
            const cW = stripSwipeContainerW.current || 300;

            // Track translateX:
            //   dir="left":  track=[current|next], starts at 0, target=-cW. delta is negative.
            //   dir="right": track=[prev|current], starts at -cW, target=0. delta is positive.
            let trackTranslate = 0;
            if (stripSwipeDir === "left") {
              trackTranslate = Math.max(-cW, Math.min(0, stripSwipeDelta));
            } else if (stripSwipeDir === "right") {
              trackTranslate = Math.max(-cW, Math.min(0, -cW + stripSwipeDelta));
            }

            function resetSwipe() {
              setStripSwipeDelta(0);
              setStripSwipeDir(null);
              setStripSwipeAnimating(false);
              stripSwipeStartX.current = null;
              stripSwipeDirRef.current = null;
            }

            return (
              <div style={{ userSelect: "none", margin: "0 -20px" }}>
                {/* Month nav header — centered label, equal-width chevron hit targets */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: 8, padding: "0 20px" }}>
                  <button
                    type="button"
                    onClick={() => setStripMonthOffset(o => Math.max(0, o - 1))}
                    disabled={stripMonthOffset === 0}
                    style={{ background: "none", border: "none", minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center", cursor: stripMonthOffset === 0 ? "default" : "pointer", color: stripMonthOffset === 0 ? "#ccc" : "#111", fontFamily: "inherit", WebkitTapHighlightColor: "transparent", flexShrink: 0 }}
                  >
                    <svg width="10" height="16" viewBox="0 0 10 16" fill="none"><path d="M8 2L2 8l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  <span style={{ flex: 1, textAlign: "center", fontSize: 15, fontWeight: 700, color: "#111", letterSpacing: -0.2 }}>{MONTH_FULL[curMonth]} {curYear}</span>
                  <button
                    type="button"
                    onClick={() => setStripMonthOffset(o => o + 1)}
                    style={{ background: "none", border: "none", minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#111", fontFamily: "inherit", WebkitTapHighlightColor: "transparent", flexShrink: 0 }}
                  >
                    <svg width="10" height="16" viewBox="0 0 10 16" fill="none"><path d="M2 2l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
                {/* Day-of-week column headers (fixed, outside sliding track) */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 2 }}>
                  {COL_LABELS.map((l, i) => (
                    <div key={i} style={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: "#0A0A0A", padding: "2px 0 6px" }}>{l}</div>
                  ))}
                </div>
                {/* Swipe viewport — clips the sliding track */}
                <div
                  style={{ overflow: "hidden", position: "relative" }}
                  onTouchStart={(e) => {
                    if (stripSwipeAnimating) return;
                    stripSwipeStartX.current = e.touches[0].clientX;
                    stripSwipeContainerW.current = e.currentTarget.offsetWidth;
                    stripSwipeDirRef.current = null;
                    setStripSwipeDir(null);
                    setStripSwipeDelta(0);
                  }}
                  onTouchMove={(e) => {
                    if (stripSwipeStartX.current === null || stripSwipeAnimating) return;
                    const dx = e.touches[0].clientX - stripSwipeStartX.current;
                    if (stripSwipeDirRef.current === null) {
                      if (Math.abs(dx) < 8) return; // wait for directional intent
                      const dir = dx < 0 ? "left" : "right";
                      if (dir === "right" && stripMonthOffset === 0) return; // floor: no backward swipe
                      stripSwipeDirRef.current = dir;
                      setStripSwipeDir(dir);
                      return; // track layout renders on next frame, set delta next move
                    }
                    if (stripSwipeDirRef.current === "right" && stripMonthOffset === 0) return;
                    setStripSwipeDelta(dx);
                  }}
                  onTouchEnd={(e) => {
                    const dir = stripSwipeDirRef.current;
                    if (stripSwipeStartX.current === null || dir === null || stripSwipeAnimating) {
                      stripSwipeStartX.current = null;
                      return;
                    }
                    const startX = stripSwipeStartX.current;
                    stripSwipeStartX.current = null;
                    const totalDx = e.changedTouches[0].clientX - startX;
                    const containerW = stripSwipeContainerW.current || 300;
                    // Committed = moved far enough in the established direction
                    const committed = Math.abs(totalDx) >= 40 &&
                      ((dir === "left" && totalDx < 0) || (dir === "right" && totalDx > 0));
                    setStripSwipeAnimating(true);
                    if (committed) {
                      // Snap to final position, then update month offset and reset
                      setStripSwipeDelta(dir === "left" ? -containerW : containerW);
                      setTimeout(() => {
                        setStripMonthOffset(o => dir === "left" ? o + 1 : Math.max(0, o - 1));
                        resetSwipe();
                      }, 300);
                    } else {
                      // Snap back to start
                      setStripSwipeDelta(0);
                      setTimeout(() => { resetSwipe(); }, 300);
                    }
                  }}
                  onTouchCancel={() => { resetSwipe(); }}
                >
                  {/* Sliding track — 200% wide during swipe, holds two months side by side */}
                  <div style={{
                    display: "flex",
                    width: isSwipeActive ? "200%" : "100%",
                    transform: isSwipeActive ? `translateX(${trackTranslate}px)` : "none",
                    transition: stripSwipeAnimating ? "transform 300ms ease-in-out" : "none",
                    willChange: "transform",
                  }}>
                    {isSwipeActive && stripSwipeDir === "right" ? (
                      /* Swiping right: [prev | current] — prev slides in from left */
                      <>
                        <div style={{ width: "50%", flexShrink: 0 }}>{renderGrid(adjCells, false)}</div>
                        <div style={{ width: "50%", flexShrink: 0 }}>{renderGrid(curCells, true)}</div>
                      </>
                    ) : isSwipeActive && stripSwipeDir === "left" ? (
                      /* Swiping left: [current | next] — next slides in from right */
                      <>
                        <div style={{ width: "50%", flexShrink: 0 }}>{renderGrid(curCells, true)}</div>
                        <div style={{ width: "50%", flexShrink: 0 }}>{renderGrid(adjCells, false)}</div>
                      </>
                    ) : (
                      renderGrid(curCells, true)
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* ── Day label ── */}
        <div className="flex items-start justify-between mb-4">
          <div>
            {isTodayOrTomorrow ? (
              <>
                <h2 className="text-[26px] font-black tracking-tight text-brand-black leading-none" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {dayHeadline}
                </h2>
                <p className="text-[18px] text-brand-text-soft font-normal mt-1 leading-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {formatFullDate(selectedDate)}
                </p>
              </>
            ) : (
              <>
                <h2 className="text-[26px] font-bold tracking-tight text-brand-black leading-none" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {dayHeadline}
                </h2>
              </>
            )}
          </div>
          <span
            className={`text-[11px] font-semibold rounded-full px-3 py-1 mt-1 leading-none ${statusPill.cls}`}
          >
            {statusPill.label}
          </span>
        </div>

      </div>{/* end date strip + day label col-span-2 wrapper */}

      {/* ── Scrollable content area (fills remaining viewport height on desktop) ── */}
      <div className={stripCalMode === "month" ? "md:flex-1" : "md:flex-1 md:overflow-y-auto"}>

      {/* ── Two-column grid on desktop ── */}
      <div className="md:grid md:grid-cols-2" style={{ background: "white" }}>

      {/* ── Events column (left on desktop) ── */}
      <div className="px-5 md:px-8 md:overflow-y-auto" style={{ paddingTop: 16, paddingBottom: 0, borderRight: "1px solid #EFEFEF" }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] font-bold uppercase tracking-widest text-[#111]">Events</span>
          <button className="md:hidden" onClick={openEventForm} style={{ background: "#F55C00", border: "none", borderRadius: 8, padding: "6px 14px", color: "white", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>+ Add Event</button>
        </div>
        <div className="flex flex-col gap-3 mb-7">
          {dayEvents.length === 0 ? (
            <p className="text-brand-text-soft text-[14px]">No events scheduled</p>
          ) : (
            dayEvents.map((ev) => {
              const assigned = getAssignedProfiles(ev, eventStaff, allProfiles);
              const status = getEventStatus(ev, eventStaff, allProfiles);
              const flagLabel = FLAG_LABELS[status];
              const timeLabel = ev.start_time
                ? `${fmt(ev.start_time)}${ev.end_time ? ` — ${fmt(ev.end_time)}` : ""}`
                : "";
              const staffChips = assigned.map((p) => ({
                id: p.id,
                name: p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim(),
                role: p.is_admin === true ? "admin" : p.role === "manager" ? "manager" : isHead(p) ? "head" : "assistant",
              }));

              const timeStatus = !isToday ? "upcoming"
                : ev.end_time && nowHHMM >= ev.end_time ? "finished"
                : ev.start_time && nowHHMM >= ev.start_time ? "live"
                : "upcoming";

              const statusPillNode = timeStatus === "finished"
                ? <div style={{ background: "#888", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "2px 8px", letterSpacing: 0.5, whiteSpace: "nowrap", flexShrink: 0 }}>FINISHED</div>
                : timeStatus === "live"
                ? <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#E53935", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "2px 8px", letterSpacing: 0.5, whiteSpace: "nowrap", flexShrink: 0 }}>
                    <span className="live-dot" style={{ width: 7, height: 7, borderRadius: "50%", background: "#FFFFFF", display: "inline-block" }} />
                    LIVE
                  </div>
                : null;

              return (
                <div key={ev.id} style={{ opacity: timeStatus === "finished" ? 0.4 : 1 }}>
                  <EventCard
                    time={timeLabel}
                    title={ev.title}
                    scheduledBy={ev.created_by}
                    staff={staffChips}
                    status={status}
                    flagLabel={flagLabel}
                    statusPill={statusPillNode}
                    notes={ev.notes}
                    onClick={timeStatus === "finished" ? undefined : () => setAssignEventTarget(ev)}
                    onDelete={timeStatus === "finished" ? undefined : () => setCancelEventTarget(ev)}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>{/* end events column */}

      {/* ── Lineup + Requests column (right on desktop) ── */}
      <div className="px-5 md:px-8 pb-28 md:pb-10 md:overflow-y-auto" style={{ paddingTop: 16 }}>
        {/* ── Shifts section ── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#111]">Lineup</span>
            <button className="md:hidden" onClick={openShiftForm} style={{ background: "#111", border: "none", borderRadius: 8, padding: "6px 14px", color: "#F55C00", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>+ Add Shift</button>
          </div>
          {dayShifts.length === 0 ? (
            <p className="text-brand-text-soft text-[14px]">No shifts scheduled</p>
          ) : (
            <div className="flex flex-col gap-2">
              {dayShifts.map((shift) => {
                const profile = allProfiles.find((p) => p.id === shift.employee_id);
                const name = profile
                  ? profile.name ||
                    `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
                  : shift.employee_name || "Unknown";
                const head = profile ? isHead(profile) : false;
                const isAdmin = profile?.is_admin === true;
                const isManager = profile?.role === "manager" && !isAdmin;
                const timeRange = `${fmt(shift.start_time)}${
                  shift.end_time ? ` – ${fmt(shift.end_time)}` : ""
                }`;
                const isFinished = isToday && shift.end_time && nowHHMM >= shift.end_time;

                return (
                  <div
                    key={shift.id}
                    className="flex items-start justify-between py-2.5 px-3 rounded-xl bg-gray-50 border border-brand-border"
                    style={isFinished ? { opacity: 0.4 } : undefined}
                  >
                    <div className="flex items-start gap-2.5" style={{ flex: 1, minWidth: 0 }}>
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center leading-none flex-shrink-0"
                        style={
                          isAdmin
                            ? { background: "#6B21A8", border: "2.5px solid #D8B4FE", color: "#D8B4FE", fontWeight: 700, fontSize: 9 }
                            : isManager
                            ? { background: "#111", border: "2.5px solid #F55C00", color: "#F55C00", fontWeight: 700, fontSize: 12 }
                            : head
                            ? { background: "#F55C00", border: "2.5px solid #111", color: "white", fontWeight: 700, fontSize: 12 }
                            : { background: "white", border: "2.5px solid #111", color: "#111", fontWeight: 700, fontSize: 12 }
                        }
                      >
                        {isAdmin ? "AD" : isManager ? "M" : head ? "L" : "A"}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {(() => {
                            const canEdit = !(isAdmin && shift.employee_id !== user?.id) && !isFinished;
                            return (
                              <span
                                onClick={canEdit ? () => openEditShift(shift) : undefined}
                                style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.2, color: "#111", cursor: canEdit ? "pointer" : "default", textDecoration: canEdit ? "underline" : "none", textDecorationColor: canEdit ? "#DDD" : "transparent", textUnderlineOffset: 2, display: "inline-flex", alignItems: "center", gap: 4 }}
                              >{name}{canEdit && <i className="ti ti-pencil" style={{ fontSize: 12, color: "#F55C00", flexShrink: 0 }} />}</span>
                            );
                          })()}
                          {shift.employee_id === user?.id && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#F55C00", letterSpacing: 0.5, fontFamily: "'Oswald', sans-serif" }}>YOU</span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                        <span className="font-mono text-[11px]" style={{ color: "#0A0A0A", fontWeight: 700 }}>{timeRange}</span>
                        {isFinished && <span style={{ fontSize: 11, color: "#999", marginTop: 1 }}>Finished</span>}
                      </div>

                      <span
                        className="text-[10px] rounded-full px-2 py-0.5 leading-none"
                        style={
                          isAdmin
                            ? { background: "#6B21A8", border: "2.5px solid #D8B4FE", color: "#D8B4FE", fontWeight: 700 }
                            : isManager
                            ? { background: "#111", border: "2.5px solid #F55C00", color: "#F55C00", fontWeight: 700 }
                            : head
                            ? { background: "#F55C00", border: "2.5px solid #111", color: "white", fontWeight: 700 }
                            : { background: "white", border: "2.5px solid #111", color: "#111", fontWeight: 700 }
                        }
                      >
                        {isAdmin ? "ADMN" : isManager ? "MNGR" : head ? "LEAD" : "ASST"}
                      </span>
                      {!(isAdmin && shift.employee_id !== user?.id) && (
                        <button
                          type="button"
                          onClick={() => setDeleteShiftTarget(shift)}
                          className="text-gray-300 hover:text-brand-red transition-colors ml-1"
                          aria-label="Delete shift"
                          style={isFinished ? { pointerEvents: "none" } : undefined}
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      </div>{/* end two-column grid */}

      {/* ── Requests section (below grid, spans full width) ── */}
      {(actionableRequests.length > 0 || approvedTimeOffRequests.length > 0 || true) && (
        <div className="px-5 md:px-8 pb-28 md:pb-10" style={{ paddingTop: 24, borderTop: "1px solid #EFEFEF" }}>
          <div className="mb-3">
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#111]">Requests</span>
          </div>
          <div className="flex flex-col gap-2">
            {actionableRequests.length === 0 && approvedTimeOffRequests.length === 0 && (
              <div style={{ fontSize: 13, color: "#AAAAAA", padding: "8px 0", fontFamily: "inherit" }}>No pending requests.</div>
            )}
            {actionableRequests.map(r => {
              const typeLabel = r.type === "swap" ? "Swap request" : r.type === "cover" ? "Cover request" : "Time off";
              const dateStr = r.type === "timeoff"
                ? (() => {
                    const start = fmtShortDate(r.date);
                    const end = r.end_date && r.end_date !== r.date ? fmtShortDate(r.end_date) : null;
                    return end ? `${start} – ${end}` : start;
                  })()
                : fmtReqDate(r.shift_date || r.date);
              const coverTimeStr = r.type === "cover" && (r.shift_start || r.shift_end)
                ? `${fmt(r.shift_start)} – ${fmt(r.shift_end)}`
                : null;
              return (
                <div key={r.id} className="flex items-center justify-between py-3 px-3 rounded-xl bg-gray-50 border border-brand-border">
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>{r.requester_name}</div>
                    {r.type === "cover" ? (
                      <>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#F55C00", fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 }}>Cover Request</div>
                        <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{dateStr}{coverTimeStr ? ` · ${coverTimeStr}` : ""}</div>
                      </>
                    ) : r.type === "timeoff" ? (
                      <>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#F55C00", fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 }}>Time Off</div>
                        <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{dateStr}</div>
                      </>
                    ) : (
                      <div style={{ fontSize: 12, color: "#111", marginTop: 2 }}>{typeLabel}{dateStr ? ` · ${dateStr}` : ""}</div>
                    )}
                    {r.reason && <div style={{ fontSize: 12, color: "#111", fontStyle: "italic", marginTop: 2 }}>{r.reason}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => r.type === "cover" ? setCoverAssignSheet({ request: r, selectedEmployeeId: null }) : setRequestConfirm({ id: r.id, name: r.requester_name, typeLabel, action: "approved", dateStr, reason: r.reason })}
                      style={{ background: "#2E7D32", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "5px 10px", border: "none", cursor: "pointer", fontFamily: "inherit" }}
                    >APPROVE</button>
                    <button
                      onClick={() => setRequestConfirm({ id: r.id, name: r.requester_name, typeLabel, action: "denied", dateStr, reason: r.reason })}
                      style={{ background: "#E53935", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "5px 10px", border: "none", cursor: "pointer", fontFamily: "inherit" }}
                    >DENY</button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Approved time off (collapsible) ── */}
          {approvedTimeOffRequests.length > 0 && (
            <div style={{ marginTop: actionableRequests.length > 0 ? 12 : 0 }}>
              <button
                type="button"
                onClick={() => setShowApproved(v => !v)}
                style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", width: "100%" }}
              >
                <span style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Approved Time Off</span>
                <span style={{ background: "#F55C00", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "2px 7px", lineHeight: 1.4 }}>{approvedTimeOffRequests.length}</span>
                <span style={{ marginLeft: "auto", fontSize: 13, color: "#888" }}>{showApproved ? "▲" : "▼"}</span>
              </button>
              {showApproved && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                  {approvedTimeOffRequests.map(r => {
                    const today = new Date().toISOString().split("T")[0];
                    const start = fmtShortDate(r.date);
                    const end = r.end_date && r.end_date !== r.date ? fmtShortDate(r.end_date) : null;
                    const dateStr = end ? `${start} – ${end}` : start;
                    const prof = allProfiles.find(p => p.id === r.requester_id);
                    const profIsAdmin = prof?.is_admin === true;
                    const isManager = prof?.role === "manager" && !profIsAdmin;
                    const isLead = !isManager && !profIsAdmin && (prof?.staff_type === "lead" || prof?.staff_type === "head");
                    const roleLabel = profIsAdmin ? "ADMN" : isManager ? "MNGR" : isLead ? "LEAD" : "ASST";
                    const rolePillStyle = profIsAdmin
                      ? { fontSize: 10, fontWeight: 700, color: "#D8B4FE", background: "#6B21A8", borderRadius: 20, padding: "3px 10px", flexShrink: 0 }
                      : isManager
                      ? { fontSize: 10, fontWeight: 700, color: "#F55C00", background: "#111", borderRadius: 20, padding: "3px 10px", flexShrink: 0 }
                      : isLead
                      ? { fontSize: 10, fontWeight: 700, color: "#111", background: "#F55C00", borderRadius: 20, padding: "3px 10px", flexShrink: 0 }
                      : { fontSize: 10, fontWeight: 700, color: "#111", background: "#fff", border: "1.5px solid #111", borderRadius: 20, padding: "3px 10px", flexShrink: 0 };
                    return (
                      <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 12, background: "#F9F9F9", border: "1px solid #EFEFEF" }}>
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{r.requester_name}</span>
                            <span style={rolePillStyle}>{roleLabel}</span>
                          </div>
                          <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>Time off{dateStr ? ` · ${dateStr}` : ""}</div>
                          {r.reason && <div style={{ fontSize: 11, color: "#F55C00", marginTop: 2, fontFamily: "'Oswald', sans-serif" }}>{r.reason}</div>}
                        </div>
                        {r.date > today && !isManager && (
                          <button
                            onClick={() => setRevokeTarget(r)}
                            style={{ background: "white", color: "#E53935", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "5px 10px", border: "1.5px solid #E53935", cursor: "pointer", fontFamily: "inherit" }}
                          >REVOKE</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      </div>{/* end scrollable content area */}

      {/* ── Add Shift bottom sheet ── */}
      {shiftFormOpen && (
        <div
          className="fixed inset-0"
          style={{ background: "rgba(0,0,0,0.5)", zIndex: 1000 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeShiftForm(); }}
          onTouchStart={(e) => { if (e.target === e.currentTarget) closeShiftForm(); }}
        >
          <div
            className="fixed bottom-0 left-0 right-0"
            style={{ background: "white", borderRadius: "20px 20px 0 0", maxHeight: "60vh", display: "flex", flexDirection: "column", transform: shiftSheetVisible ? "translateY(0)" : "translateY(100%)", transition: "transform 300ms ease", zIndex: 1001 }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div style={{ width: 36, height: 4, background: "#E0E0E0", borderRadius: 2, margin: "10px auto 4px" }} />

            {/* Header */}
            <div style={{ padding: "10px 20px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #EFEFEF", flexShrink: 0 }}>
              {(() => {
                const sortedDates = [...shiftDates].sort();
                const fmtDate = (ds) => {
                  const [y, m, d] = ds.split("-").map(Number);
                  const dt = new Date(y, m - 1, d);
                  const wd = dt.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
                  const mo = dt.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
                  return `${wd}, ${mo} ${d}`;
                };
                const loc = (currentLocation || "").toUpperCase();
                let dateLine = null;
                if (sortedDates.length > 0) {
                  const shown = sortedDates.slice(0, 3).map(fmtDate).join(" · ");
                  const extra = sortedDates.length > 3 ? ` · +${sortedDates.length - 3}` : "";
                  dateLine = shown + extra;
                }
                const baseStyle = { fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", fontWeight: 700 };
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, marginRight: 12 }}>
                    <span style={{ ...baseStyle, fontSize: 17, color: "#F55C00", letterSpacing: 0.5 }}>NEW SHIFT</span>
                    {dateLine && <span style={{ ...baseStyle, fontSize: 13, color: "#111", letterSpacing: 0.3 }}>{dateLine}</span>}
                    {loc && <span style={{ ...baseStyle, fontSize: 13, color: "#F55C00", letterSpacing: 0.3 }}>{loc}</span>}
                  </div>
                );
              })()}
              <button type="button" onClick={closeShiftForm} style={{ background: "none", border: "none", fontSize: 15, color: "#888", cursor: "pointer", fontFamily: "inherit", fontWeight: 500, flexShrink: 0 }}>Cancel</button>
            </div>

            {/* Scrollable body */}
            <div style={{ overflowY: "auto", flex: 1, padding: "20px 20px 48px", WebkitOverflowScrolling: "touch" }}>

              {/* STAFF */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                  Staff <span style={{ color: "#F55C00", fontSize: 9 }}>REQUIRED</span>
                </div>
                {employeeProfiles.length === 0 && (
                  <div style={{ fontSize: 13, color: "#AAAAAA", padding: "12px 0", fontFamily: "inherit" }}>No staff assigned to this location. Invite staff first.</div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    ...employeeProfiles.filter(p => p.is_admin === true).sort((a, b) => (b.cit_stars || 0) - (a.cit_stars || 0)),
                    ...employeeProfiles.filter(p => p.role?.toLowerCase() === "manager" && p.is_admin !== true).sort((a, b) => (b.cit_stars || 0) - (a.cit_stars || 0)),
                    ...employeeProfiles.filter(p => isHead(p) && p.role?.toLowerCase() !== "manager" && p.is_admin !== true).sort((a, b) => (b.cit_stars || 0) - (a.cit_stars || 0)),
                    ...employeeProfiles.filter(p => !isHead(p) && p.role?.toLowerCase() !== "manager" && p.is_admin !== true).sort((a, b) => (b.cit_stars || 0) - (a.cit_stars || 0)),
                  ].map(p => {
                    const name = p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim();
                    const firstName = p.first_name || name.split(" ")[0];
                    const selected = shiftForm.employee_id === p.id;
                    const pIsAdmin = p.is_admin === true;
                    const badgeLetter = pIsAdmin ? "AD" : p.role?.toLowerCase() === "manager" ? "M" : isHead(p) ? "L" : "A";
                    const badgeStyle = pIsAdmin
                      ? { background: "#6B21A8", border: "2.5px solid #D8B4FE", color: "#D8B4FE" }
                      : badgeLetter === "M"
                      ? { background: "#111", border: "2.5px solid #F55C00", color: "#F55C00" }
                      : badgeLetter === "L"
                      ? { background: "#F55C00", border: "2.5px solid #111", color: "white" }
                      : { background: "white", border: "2.5px solid #111", color: "#111" };
                    const roleLabel = pIsAdmin ? "Admin" : p.role?.toLowerCase() === "manager" ? "Manager" : isHead(p) ? "Lead" : "Assistant";
                    const DAY_ORDER = [0,1,2,3,4,5,6];
                    const DAY_LABELS = ["MON","TUE","WED","THU","FRI","SAT","SUN"];
                    const fmtTime = t => { const [h, m] = t.split(':'); const hr = parseInt(h); return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`; };
                    return (
                      <div key={p.id}>
                        <div onClick={() => setShiftForm(f => ({ ...f, employee_id: f.employee_id === p.id ? "" : p.id }))}
                          style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: selected && selectedShiftAvailability.length > 0 ? "12px 12px 0 0" : 12, border: selected ? "2px solid #F55C00" : "1.5px solid #EFEFEF", borderBottom: selected && selectedShiftAvailability.length > 0 ? "none" : undefined, background: selected ? "#FFF4EE" : "white", cursor: "pointer", transition: "all 0.15s" }}>
                          <span style={{ ...badgeStyle, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", fontWeight: 700, fontSize: pIsAdmin ? 9 : 11, flexShrink: 0 }}>{badgeLetter}</span>
                          <span style={{ fontSize: 14, fontWeight: 600, color: "#0A0A0A", flex: 1, display: "flex", alignItems: "center", gap: 6 }}>
                            {name}
                            {p.id === user?.id && (
                              <span style={{ fontSize: 10, fontWeight: 700, color: "#F55C00", letterSpacing: 0.5, fontFamily: "'Oswald', sans-serif" }}>YOU</span>
                            )}
                          </span>
                          {roleLabel === "Admin"
                            ? <span style={{ fontSize: 10, fontWeight: 700, color: "#D8B4FE", background: "#6B21A8", borderRadius: 20, padding: "3px 10px", flexShrink: 0 }}>ADMN</span>
                            : roleLabel === "Manager"
                            ? <span style={{ fontSize: 10, fontWeight: 700, color: "#F55C00", background: "#111111", borderRadius: 20, padding: "3px 10px", flexShrink: 0 }}>MNGR</span>
                            : roleLabel === "Lead"
                            ? <span style={{ fontSize: 10, fontWeight: 700, color: "#111111", background: "#F55C00", borderRadius: 20, padding: "3px 10px", flexShrink: 0 }}>LEAD</span>
                            : <span style={{ fontSize: 10, fontWeight: 700, color: "#111111", background: "#ffffff", border: "1.5px solid #111111", borderRadius: 20, padding: "3px 10px", flexShrink: 0 }}>ASST</span>
                          }
                        </div>
                        {selected && selectedShiftAvailability.length > 0 && (
                          <div style={{ background: "#FFF4EE", border: "2px solid #F55C00", borderTop: "1px solid #F5DCC8", borderRadius: "0 0 12px 12px", padding: "10px 14px 12px" }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "#F55C00", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6, fontFamily: "'Oswald', sans-serif" }}>{firstName}'s Availability</div>
                            {DAY_ORDER.map(dow => {
                              const row = selectedShiftAvailability.find(r => r.day_of_week === dow);
                              if (!row) return null;
                              return (
                                <div key={dow} style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 0" }}>
                                  <span style={{ color: "#111", fontSize: 11, fontWeight: 600, minWidth: 32 }}>{DAY_LABELS[dow]}</span>
                                  {row.is_available
                                    ? <span style={{ color: "#111", fontSize: 12, fontWeight: 500 }}>{fmtTime(row.start_time)} – {fmtTime(row.end_time)}</span>
                                    : <span style={{ color: "#999", fontSize: 12 }}>—</span>
                                  }
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* DATE */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1 }}>Date <span style={{ color: "#F55C00", fontSize: 9 }}>REQUIRED</span></div>
                  <div onClick={() => { setShiftMultiDay(m => !m); if (shiftMultiDay) setShiftDates(new Set([...shiftDates].slice(0,1))); }}
                    style={{ display: "flex", alignItems: "center", background: "#f4f4f4", borderRadius: 20, padding: "3px 4px", cursor: "pointer", gap: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: !shiftMultiDay ? "#111" : "transparent", color: !shiftMultiDay ? "white" : "#888", transition: "all 0.15s" }}>Single</span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: shiftMultiDay ? "#F55C00" : "transparent", color: shiftMultiDay ? "white" : "#888", transition: "all 0.15s" }}>Multiple</span>
                  </div>
                </div>
                <InfiniteFormDateStrip
                  selectedDates={shiftDates}
                  onToggle={(dateStr) => {
                    if (shiftMultiDay) {
                      setShiftDates(prev => { const s = new Set(prev); s.has(dateStr) ? s.delete(dateStr) : s.add(dateStr); return s; });
                    } else {
                      setShiftDates(new Set([dateStr]));
                    }
                    setShiftConflictDates(new Set());
                    setShiftConflictDetails([]);
                  }}
                  blockedDates={shiftBlockedDates}
                  conflictDates={shiftConflictDates}
                />
                {formatSelectedSummary(shiftDates) && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#999", lineHeight: 1.4 }}>
                    {formatSelectedSummary(shiftDates)}
                  </div>
                )}
                {shiftConflictDetails.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {shiftConflictDetails.map(c => {
                      const p = allProfiles.find(x => x.id === shiftForm.employee_id);
                      return (
                        <p key={c.date} style={{ color: "#E53935", fontSize: 12, margin: "3px 0", lineHeight: 1.4 }}>
                          {p?.name || "Staff"} {c.reason} on {fmtReqDate(c.date)}.
                        </p>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* TIME */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                  Time <span style={{ color: "#F55C00", fontSize: 9 }}>REQUIRED</span>
                </div>
                {shiftError === "End time must be after start time." && (
                  <p style={{ color: "#E53935", fontSize: 13, marginBottom: 8 }}>{shiftError}</p>
                )}
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <DrumPicker label="Start" value={shiftForm.start_time} onChange={v => {
                      setShiftForm(f => ({ ...f, start_time: v }));
                    }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <DrumPicker label="End" value={shiftForm.end_time} onChange={v => setShiftForm(f => ({ ...f, end_time: v }))} />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <button type="button" onClick={(e) => { e.preventDefault = e.preventDefault || (() => {}); submitShift(e); }} disabled={shiftSubmitting || shiftSuccess}
                style={{ width: "100%", background: shiftSuccess ? "#2E7D32" : shiftSubmitting ? "#f5a87a" : "#F55C00", color: "white", border: "none", borderRadius: 14, padding: "16px", fontSize: 15, fontWeight: 700, cursor: (shiftSubmitting || shiftSuccess) ? "default" : "pointer", fontFamily: "inherit" }}>
                {shiftSuccess ? "Shift added ✓" : shiftSubmitting ? "Saving…" : "ADD SHIFT"}
              </button>

              {/* Error */}
              {shiftError && shiftError !== "End time must be after start time." && (
                <p style={{ color: "#E53935", fontSize: 13, marginBottom: 12 }}>{shiftError}</p>
              )}

            </div>
          </div>
        </div>
      )}

      {/* ── Add Event bottom sheet ── */}
      {eventFormOpen && (
        <div
          className="fixed inset-0"
          style={{ background: "rgba(0,0,0,0.5)", zIndex: 1000 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeEventForm(); }}
          onTouchStart={(e) => { if (e.target === e.currentTarget) closeEventForm(); }}
        >
          <div
            className="fixed bottom-0 left-0 right-0"
            style={{ background: "white", borderRadius: "20px 20px 0 0", maxHeight: "60vh", display: "flex", flexDirection: "column", transform: eventSheetVisible ? "translateY(0)" : "translateY(100%)", transition: "transform 300ms ease", zIndex: 1001 }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => { if (!e.target.closest('.drum-picker')) e.stopPropagation(); }}
          >
            {/* Drag handle */}
            <div style={{ width: 36, height: 4, background: "#E0E0E0", borderRadius: 2, margin: "10px auto 4px" }} />

            {/* Header */}
            <div style={{ padding: "10px 20px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #EFEFEF", flexShrink: 0 }}>
              {(() => {
                const sortedDates = [...evDates].sort();
                const fmtDate = (ds) => {
                  const [y, m, d] = ds.split("-").map(Number);
                  const dt = new Date(y, m - 1, d);
                  const wd = dt.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
                  const mo = dt.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
                  return `${wd}, ${mo} ${d}`;
                };
                const loc = (currentLocation || "").toUpperCase();
                const DAY_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
                let dateLine = null;
                if (evRecurring) {
                  const uniqueWeekdays = [...new Set(sortedDates.map(ds => new Date(ds + "T00:00:00").getDay()))].sort((a, b) => a - b);
                  if (uniqueWeekdays.length > 0) {
                    let recurLine = "EVERY " + uniqueWeekdays.map(n => DAY_NAMES[n]).join(" · ");
                    if (evRecurringEnd) {
                      const ed = new Date(evRecurringEnd + "T00:00:00");
                      const mo = ed.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
                      recurLine += ` · UNTIL ${mo} ${ed.getDate()}`;
                    }
                    dateLine = recurLine;
                  }
                } else if (sortedDates.length > 0) {
                  const shown = sortedDates.slice(0, 3).map(fmtDate).join(" · ");
                  const extra = sortedDates.length > 3 ? ` · +${sortedDates.length - 3}` : "";
                  dateLine = shown + extra;
                }
                const baseStyle = { fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", fontWeight: 700 };
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, marginRight: 12 }}>
                    <span style={{ ...baseStyle, fontSize: 17, color: "#F55C00", letterSpacing: 0.5 }}>NEW EVENT</span>
                    {(evTitle.trim() || dateLine) && <span style={{ ...baseStyle, fontSize: 13, color: "#111", letterSpacing: 0.3 }}>{[evTitle.trim().toUpperCase(), dateLine].filter(Boolean).join(" · ")}</span>}
                    {loc && <span style={{ ...baseStyle, fontSize: 13, color: "#F55C00", letterSpacing: 0.3 }}>{loc}</span>}
                    {evStartTime && evEndTime && (() => {
                      const fmtT = (t) => { const [h, m] = t.split(":"); const hr = parseInt(h, 10); return `${hr > 12 ? hr - 12 : hr === 0 ? 12 : hr}:${m}${hr >= 12 ? "PM" : "AM"}`; };
                      return <span style={{ ...baseStyle, fontSize: 13, color: "#111", letterSpacing: 0.3, }}>{fmtT(evStartTime)} - {fmtT(evEndTime)}</span>;
                    })()}
                  </div>
                );
              })()}
              <button type="button" onClick={closeEventForm} style={{ background: "none", border: "none", fontSize: 15, color: "#888", cursor: "pointer", fontFamily: "inherit", fontWeight: 500, flexShrink: 0 }}>Cancel</button>
            </div>

            {/* Scrollable body */}
            <div style={{ overflowY: "auto", flex: 1, padding: "20px 20px 48px", WebkitOverflowScrolling: "touch" }}>

              {/* TITLE */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                  Title <span style={{ color: "#F55C00", fontSize: 9 }}>REQUIRED</span>
                </div>
                <input type="text" value={evTitle} onChange={e => setEvTitle(e.target.value)} placeholder="Event title"
                  style={{ width: "100%", background: "#f9f9f9", border: "1px solid #E0E0E0", color: "#111", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
                />
              </div>

              {/* DATE */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1 }}>Date <span style={{ color: "#F55C00", fontSize: 9 }}>REQUIRED</span></div>
                  <div onClick={() => { setEventMultiDay(m => !m); if (eventMultiDay) setEvDates(new Set([...evDates].slice(0,1))); }}
                    style={{ display: "flex", alignItems: "center", background: "#f4f4f4", borderRadius: 20, padding: "3px 4px", cursor: "pointer", gap: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: !eventMultiDay ? "#111" : "transparent", color: !eventMultiDay ? "white" : "#888", transition: "all 0.15s" }}>Single</span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: eventMultiDay ? "#F55C00" : "transparent", color: eventMultiDay ? "white" : "#888", transition: "all 0.15s" }}>Multiple</span>
                  </div>
                </div>
                <InfiniteFormDateStrip
                  selectedDates={evDates}
                  onToggle={(dateStr) => {
                    if (eventMultiDay) {
                      toggleEvDate(dateStr);
                    } else {
                      setEvDates(new Set([dateStr]));
                    }
                  }}
                />
                {formatSelectedSummary(evDates) && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#999", lineHeight: 1.4 }}>
                    {formatSelectedSummary(evDates)}
                  </div>
                )}
              </div>

              {/* TIME */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                  Time <span style={{ color: "#F55C00", fontSize: 9 }}>REQUIRED</span>
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}><DrumPicker label="Start" value={evStartTime} onChange={v => { setEvStartTime(v); setEvEndTime(addHour(v)); }} /></div>
                  <div style={{ flex: 1 }}><DrumPicker label="End" value={evEndTime} onChange={setEvEndTime} /></div>
                </div>
              </div>

              {/* NOTES */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                  Notes <span style={{ color: "#888", fontSize: 9 }}>OPTIONAL</span>
                </div>
                <textarea value={evNotes} onChange={e => setEvNotes(e.target.value)} placeholder="Any additional details…" rows={3}
                  style={{ width: "100%", background: "#f9f9f9", border: "1px solid #E0E0E0", color: "#111", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", resize: "none", boxSizing: "border-box" }}
                />
              </div>

              {/* RECURRING */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: evRecurring ? 16 : 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1 }}>Recurring</div>
                  <div onClick={() => setEvRecurring(r => !r)} style={{ width: 44, height: 26, borderRadius: 13, background: evRecurring ? "#F55C00" : "#ddd", position: "relative", cursor: "pointer", transition: "background 0.2s", flexShrink: 0 }}>
                    <div style={{ position: "absolute", top: 3, left: evRecurring ? 21 : 3, width: 20, height: 20, borderRadius: "50%", background: "white", boxShadow: "0 1px 3px rgba(0,0,0,0.4)", transition: "left 0.2s" }} />
                  </div>
                </div>
                {evRecurring && (
                  <div style={{ background: "#fafafa", borderRadius: 12, padding: 16, border: "1.5px solid #EFEFEF" }}>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>Select end date — only days matching your selected weekdays are enabled.</div>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4, minHeight: 22 }}>
                      {(() => { const now = new Date(); return (evRecurCalMonth.year !== now.getFullYear() || evRecurCalMonth.month !== now.getMonth()) && (
                        <button type="button" onClick={() => setEvRecurCalMonth({ year: now.getFullYear(), month: now.getMonth() })}
                          style={{ fontSize: 11, fontWeight: 600, color: "#555", background: "white", border: "1px solid #DDD", borderRadius: 20, padding: "3px 10px", cursor: "pointer", fontFamily: "inherit", WebkitTapHighlightColor: "transparent" }}>
                          ← Back to Today
                        </button>
                      ); })()}
                    </div>
                    <RecurringCalendar
                      selectedWeekdays={new Set([...evDates].map(d => new Date(d + "T00:00:00").getDay()))}
                      minDate={(() => {
                        if (evDates.size === 0) return undefined;
                        const earliest = [...evDates].sort()[0];
                        const d = new Date(earliest + "T00:00:00");
                        d.setDate(d.getDate() + 7);
                        return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                      })()}
                      blockedDates={evDates}
                      value={evRecurringEnd}
                      onChange={setEvRecurringEnd}
                      calMonth={evRecurCalMonth}
                      onChangeMonth={dir => setEvRecurCalMonth(prev => {
                        let m = prev.month + dir; let y = prev.year;
                        if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
                        return { year: y, month: m };
                      })}
                    />
                    {evRecurringEnd && (
                      <div style={{ marginTop: 10, fontSize: 12, color: "#F55C00", fontWeight: 600 }}>Repeats until {FULL_MONTH_NAMES[new Date(evRecurringEnd + "T00:00:00").getMonth()]} {new Date(evRecurringEnd + "T00:00:00").getDate()}</div>
                    )}
                  </div>
                )}
              </div>

              {/* Error */}
              {eventError && <p style={{ color: "#E53935", fontSize: 13, marginBottom: 12 }}>{eventError}</p>}

              {/* Submit */}
              <button type="button" onClick={submitEvent} disabled={eventSubmitting}
                style={{ width: "100%", background: eventSubmitting ? "#f5a87a" : "#F55C00", color: "white", border: "none", borderRadius: 14, padding: "16px", fontSize: 15, fontWeight: 700, cursor: eventSubmitting ? "default" : "pointer", fontFamily: "inherit" }}>
                {eventSubmitting ? "Saving…" : "ADD EVENT"}
              </button>

            </div>
          </div>
        </div>
      )}

      {/* ── Delete shift confirm ── */}
      {/* ── Cancel invite confirm modal ── */}
      {cancelInviteConfirm && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2100, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 24px" }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setCancelInviteConfirm(null); }}
        >
          <div style={{ background: "white", borderRadius: 16, padding: "28px 24px", maxWidth: 340, width: "100%" }}>
            <p style={{ fontSize: 16, fontWeight: 700, color: "#111", marginBottom: 12, lineHeight: 1.3, fontFamily: "'Oswald', sans-serif" }}>
              CANCEL INVITE FOR {((cancelInviteConfirm.first_name && cancelInviteConfirm.last_name) ? `${cancelInviteConfirm.first_name} ${cancelInviteConfirm.last_name}` : cancelInviteConfirm.name || "").toUpperCase()}?
            </p>
            <p style={{ fontSize: 13, color: "#111", marginBottom: 24, lineHeight: 1.5 }}>This will cancel their pending invite. You can re-invite them at any time.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => setCancelInviteConfirm(null)}
                style={{ flex: 1, background: "white", color: "#111", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "10px", border: "1.5px solid #111", cursor: "pointer", fontFamily: "inherit" }}
              >KEEP</button>
              <button
                type="button"
                onClick={async () => {
                  console.log("[cancel-invite] cancel invite button clicked", cancelInviteConfirm);
                  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
                  const { data: { session } } = await supabase.auth.getSession();
                  console.log("[cancel-invite] payload:", { profile_id: cancelInviteConfirm.id, email: cancelInviteConfirm.email });
                  const res = await fetch(`${supabaseUrl}/functions/v1/cancel-invite`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session?.access_token}` },
                    body: JSON.stringify({ profile_id: cancelInviteConfirm.id, email: cancelInviteConfirm.email }),
                  });
                  setCancelInviteConfirm(null);
                  if (res.ok) {
                    onShiftAdded?.();
                  }
                }}
                style={{ flex: 1, background: "#E53935", color: "white", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "10px", border: "none", cursor: "pointer", fontFamily: "inherit" }}
              >CANCEL INVITE</button>
            </div>
          </div>
        </div>
      )}

      {deleteShiftTarget && (
        <ConfirmModal
          title="Delete shift?"
          message={(() => {
            const p = allProfiles.find((x) => x.id === deleteShiftTarget.employee_id);
            const name = p ? (p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim()) : deleteShiftTarget.employee_name;
            return `${name} · ${fmt(deleteShiftTarget.start_time)} – ${fmt(deleteShiftTarget.end_time)}`;
          })()}
          confirmLabel="Delete"
          onConfirm={confirmDeleteShift}
          onCancel={() => setDeleteShiftTarget(null)}
          busy={deletingShift}
        />
      )}

      {/* ── Cancel event confirm ── */}
      {cancelEventTarget && (
        <ConfirmModal
          title="Cancel event?"
          message={null}
          subtitle={<><span style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>{cancelEventTarget.title}</span>{cancelEventTarget.start_time && <span style={{ fontSize: 13, color: "#F55C00", fontWeight: 600, display: "block", marginTop: 2 }}>{fmt(cancelEventTarget.start_time)}{cancelEventTarget.end_time ? ` – ${fmt(cancelEventTarget.end_time)}` : ""}</span>}</>}
          confirmLabel="Cancel Event"
          onConfirm={confirmCancelEvent}
          onCancel={() => setCancelEventTarget(null)}
          busy={cancellingEvent}
        />
      )}

      {/* ── Assign staff sheet ── */}
      {assignEventTarget && (() => {
        const ev = assignEventTarget;
        const assignedIds = new Set(
          eventStaff.filter((es) => es.event_id === ev.id).map((es) => es.employee_id)
        );

        // Find overlapping events on the same date (exclude this event)
        const overlappingEventIds = new Set(
          events
            .filter(
              (other) =>
                other.id !== ev.id &&
                other.date === ev.date &&
                other.start_time < ev.end_time &&
                other.end_time > ev.start_time
            )
            .map((other) => other.id)
        );

        // Busy staff: assigned to an overlapping event and not already on this event
        const busyProfileIds = new Set(
          eventStaff
            .filter((es) => overlappingEventIds.has(es.event_id))
            .map((es) => es.employee_id)
            .filter((id) => !assignedIds.has(id))
        );

        // Staff with a shift on this date at the event's location covering the full event duration
        const shiftProfileIds = new Set(
          shifts
            .filter((s) =>
              s.date === ev.date &&
              s.location === ev.location &&
              s.start_time <= ev.start_time &&
              s.end_time >= ev.end_time
            )
            .map((s) => s.employee_id)
        );

        const available = allProfiles
          .filter((p) => shiftProfileIds.has(p.id) && !busyProfileIds.has(p.id))
          .sort((a, b) => {
            const rank = (p) => p.role === "manager" ? 0 : isHead(p) ? 1 : 2;
            const ra = rank(a), rb = rank(b);
            if (ra !== rb) return ra - rb;
            return (b.cit_stars || 0) - (a.cit_stars || 0);
          });

        const timeLabel = ev.start_time
          ? `${fmt(ev.start_time)}${ev.end_time ? ` — ${fmt(ev.end_time)}` : ""}`
          : "";

        return (
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onMouseDown={(e) => { if (e.target === e.currentTarget) setAssignEventTarget(null); }}
            onTouchStart={(e) => { if (e.target === e.currentTarget) setAssignEventTarget(null); }}
          >
            <div
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50"
              style={{ animation: "slideUp 220ms ease", maxHeight: "80vh", overflowY: "auto" }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <div className="px-6 pt-4 pb-8">
                {/* Handle */}
                <div className="flex justify-center mb-4">
                  <div className="w-10 h-1 rounded-full bg-gray-200" />
                </div>

                {/* Header */}
                <div className="mb-5" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 className="text-[18px] font-bold tracking-tight text-brand-black leading-tight" style={{ fontFamily: "'Oswald', sans-serif", textTransform: "uppercase" }}>
                      {ev.title}
                    </h3>
                    {timeLabel && (
                      <p style={{ fontSize: 13, fontWeight: 700, color: "#0A0A0A", marginTop: 2, fontFamily: "monospace" }}>{timeLabel}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => openEditEvent(ev)}
                    style={{ flexShrink: 0, marginLeft: 12, background: "#F4F4F4", border: "none", borderRadius: 20, padding: "5px 12px", fontSize: 12, fontWeight: 600, color: "#555", cursor: "pointer", fontFamily: "inherit", WebkitTapHighlightColor: "transparent" }}
                  >
                    Edit
                  </button>
                </div>

                {/* Staff list */}
                {available.length === 0 ? (
                  <p className="text-brand-text-soft text-[14px]">No staff on shift for this event.</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {available.map((p) => {
                      const name = p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim();
                      const assigned = assignedIds.has(p.id);
                      const pIsAdm = p.is_admin === true;
                      const isMgr = p.role?.toLowerCase() === "manager" && !pIsAdm;
                      const isLead = !pIsAdm && (p.staff_type === "head" || p.staff_type === "lead" || isMgr);

                      return (
                        <div
                          key={p.id}
                          className="flex items-center justify-between py-2.5 px-3 rounded-xl bg-gray-50 border border-brand-border"
                        >
                          <div className="flex items-center gap-2.5">
                            {pIsAdm ? (
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full leading-none flex-shrink-0"
                                style={{ background: "#6B21A8", border: "2.5px solid #D8B4FE", color: "#D8B4FE", fontWeight: 700, fontSize: 10 }}>
                                AD
                              </span>
                            ) : isMgr ? (
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full leading-none flex-shrink-0"
                                style={{ background: "#111", border: "2.5px solid #F55C00", color: "#F55C00", fontWeight: 700, fontSize: 12 }}>
                                M
                              </span>
                            ) : isLead ? (
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full leading-none flex-shrink-0"
                                style={{ background: "#F55C00", border: "2.5px solid #111", color: "white", fontWeight: 700, fontSize: 12 }}>
                                L
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full leading-none flex-shrink-0"
                                style={{ background: "white", border: "2.5px solid #111", color: "#111", fontWeight: 700, fontSize: 12 }}>
                                A
                              </span>
                            )}
                            <span className="font-semibold text-[14px] text-brand-black">{name}</span>
                          </div>
                          {assigned ? (
                            <button
                              type="button"
                              onClick={() => removeStaff(p)}
                              className="text-[12px] font-semibold rounded-full px-3 py-1.5 bg-gray-100 text-brand-text-soft leading-none"
                            >
                              Remove
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => assignStaff(p)}
                              className="text-[12px] font-semibold rounded-full px-3 py-1.5 bg-brand-orange text-white leading-none"
                            >
                              Add
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Edit Event bottom sheet ── */}
      {editEventTarget && (
        <div
          className="fixed inset-0"
          style={{ background: "rgba(0,0,0,0.6)", zIndex: 1100 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeEditEvent(); }}
          onTouchStart={(e) => { if (e.target === e.currentTarget) closeEditEvent(); }}
        >
          <div
            className="fixed bottom-0 left-0 right-0"
            style={{ background: "white", borderRadius: "20px 20px 0 0", maxHeight: "70vh", display: "flex", flexDirection: "column", transform: editEventVisible ? "translateY(0)" : "translateY(100%)", transition: "transform 300ms ease", zIndex: 1101 }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div style={{ width: 36, height: 4, background: "#E0E0E0", borderRadius: 2, margin: "10px auto 4px", flexShrink: 0 }} />

            {/* Header */}
            <div style={{ padding: "10px 20px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #EFEFEF", flexShrink: 0 }}>
              {(() => {
                const t = editEventForm.type;
                const line1 = t === "Class" ? "EDIT CLASS" : t === "Birthday Party" ? "EDIT BIRTHDAY PARTY" : t === "Field Day" ? "EDIT FIELD DAY" : t === "Camp" ? "EDIT CAMP" : "EDIT EVENT";
                const fmtDate = (ds) => {
                  const [y, m, d] = ds.split("-").map(Number);
                  const dt = new Date(y, m - 1, d);
                  const wd = dt.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
                  const mo = dt.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
                  return `${wd}, ${mo} ${d}`;
                };
                const datePart = editEventForm.date ? fmtDate(editEventForm.date) : null;
                const titlePart = editEventForm.title.trim() ? editEventForm.title.trim().toUpperCase() : null;
                const line2 = [titlePart, datePart].filter(Boolean).join(" · ") || null;
                const loc = (currentLocation || "").toUpperCase();
                const fmtT = (tv) => { const [h, m] = tv.split(":"); const hr = parseInt(h, 10); return `${hr > 12 ? hr - 12 : hr === 0 ? 12 : hr}:${m}${hr >= 12 ? "PM" : "AM"}`; };
                const line4 = editEventForm.start_time && editEventForm.end_time ? `${fmtT(editEventForm.start_time)} - ${fmtT(editEventForm.end_time)}` : null;
                const baseStyle = { fontFamily: "'Oswald', sans-serif", textTransform: "uppercase", fontWeight: 700 };
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 1, flex: 1, marginRight: 12 }}>
                    <span style={{ ...baseStyle, fontSize: 17, color: "#F55C00", letterSpacing: 0.5 }}>{line1}</span>
                    {line2 && <span style={{ ...baseStyle, fontSize: 13, color: "#111", letterSpacing: 0.3 }}>{line2}</span>}
                    {loc && <span style={{ ...baseStyle, fontSize: 13, color: "#F55C00", letterSpacing: 0.3 }}>{loc}</span>}
                    {line4 && <span style={{ ...baseStyle, fontSize: 13, color: "#111", letterSpacing: 0.3 }}>{line4}</span>}
                  </div>
                );
              })()}
              <button type="button" onClick={closeEditEvent} style={{ background: "none", border: "none", fontSize: 15, color: "#888", cursor: "pointer", fontFamily: "inherit", fontWeight: 500, flexShrink: 0 }}>Cancel</button>
            </div>

            {/* Scrollable body */}
            <div style={{ overflowY: "auto", flex: 1, padding: "20px 20px 48px", WebkitOverflowScrolling: "touch" }}>

              {/* DATE */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                  Date <span style={{ color: "#F55C00", fontSize: 9 }}>REQUIRED</span>
                </div>
                <InfiniteFormDateStrip
                  selectedDates={new Set([editEventForm.date])}
                  onToggle={(dateStr) => setEditEventForm(f => ({ ...f, date: dateStr }))}
                />
                {editEventForm.date && editEventForm.date !== editEventTarget.date && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#E53935" }}>⚠ Changing the date will remove all assigned staff for this event.</div>
                )}
              </div>

              {/* TITLE */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                  Title <span style={{ color: "#F55C00", fontSize: 9 }}>REQUIRED</span>
                </div>
                <input
                  type="text"
                  value={editEventForm.title}
                  onChange={e => setEditEventForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Event title"
                  style={{ width: "100%", background: "#f9f9f9", border: "1px solid #E0E0E0", color: "#111", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
                />
              </div>

              {/* TIME */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                  Time <span style={{ color: "#F55C00", fontSize: 9 }}>REQUIRED</span>
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <DrumPicker label="Start" value={editEventForm.start_time} onChange={v => {
                      setEditEventForm(f => ({ ...f, start_time: v, end_time: v >= f.end_time ? addHour(v) : f.end_time }));
                    }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <DrumPicker label="End" value={editEventForm.end_time} onChange={v => setEditEventForm(f => ({ ...f, end_time: v }))} />
                  </div>
                </div>
              </div>

              {/* NOTES */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                  Notes <span style={{ color: "#888", fontSize: 9 }}>OPTIONAL</span>
                </div>
                <textarea
                  value={editEventForm.notes}
                  onChange={e => setEditEventForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Any additional notes..."
                  rows={3}
                  style={{ width: "100%", background: "#f9f9f9", border: "1px solid #E0E0E0", color: "#111", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", resize: "none" }}
                />
              </div>

              {/* Error */}
              {editEventError && (
                <p style={{ color: "#E53935", fontSize: 13, marginBottom: 12 }}>{editEventError}</p>
              )}

              {/* Save */}
              <button
                type="button"
                onClick={submitEditEvent}
                disabled={editEventSubmitting}
                style={{ width: "100%", background: editEventSubmitting ? "#f5a87a" : "#F55C00", color: "white", border: "none", borderRadius: 14, padding: "16px", fontSize: 15, fontWeight: 700, cursor: editEventSubmitting ? "default" : "pointer", fontFamily: "inherit" }}
              >
                {editEventSubmitting ? "Saving…" : "SAVE CHANGES"}
              </button>

            </div>
          </div>
        </div>
      )}

      {/* ── Request confirmation modal ── */}
      {requestConfirm && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 24px" }}
          onClick={() => setRequestConfirm(null)}
        >
          <div
            style={{ background: "white", borderRadius: 16, padding: "28px 24px", width: "100%", maxWidth: 340 }}
            onClick={e => e.stopPropagation()}
          >
            <p style={{ fontSize: 16, fontWeight: 600, color: "#111", marginBottom: requestConfirm.dateStr || requestConfirm.reason ? 8 : 24, lineHeight: 1.4 }}>
              {requestConfirm.action === "approved"
                ? `Approve ${requestConfirm.name}'s ${requestConfirm.typeLabel.toLowerCase()}?`
                : `Deny ${requestConfirm.name}'s ${requestConfirm.typeLabel.toLowerCase()}?`}
            </p>
            {(requestConfirm.dateStr || requestConfirm.reason) && (
              <div style={{ marginBottom: 24 }}>
                {requestConfirm.dateStr && <div style={{ fontSize: 13, color: "#F55C00", fontWeight: 600, marginBottom: 2 }}>{requestConfirm.dateStr}</div>}
                {requestConfirm.reason && <div style={{ fontSize: 13, color: "#111", fontStyle: "italic" }}>{requestConfirm.reason}</div>}
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setRequestConfirm(null)}
                style={{ flex: 1, background: "white", color: "#111", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "10px", border: "1.5px solid #111", cursor: "pointer", fontFamily: "inherit" }}
              >CANCEL</button>
              <button
                onClick={() => { onHandleRequest && onHandleRequest(requestConfirm.id, requestConfirm.action); setRequestConfirm(null); }}
                style={{ flex: 1, background: requestConfirm.action === "approved" ? "#2E7D32" : "#E53935", color: "white", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "10px", border: "none", cursor: "pointer", fontFamily: "inherit" }}
              >{requestConfirm.action === "approved" ? "APPROVE" : "DENY"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cover Assign Sheet ── */}
      {coverAssignSheet && (() => {
        const r = coverAssignSheet.request;
        const sortedProfiles = [...allProfiles]
          .filter(p => {
            if (p.id === r.requester_id) return false;
            if (p.is_admin === true) return false;
            if (p.removed === true || p.active === false) return false;
            const loc = r.shift_location || currentLocation;
            if (p.locations && p.locations.length > 0) return p.locations.includes(loc);
            return (p.location || currentLocation) === loc;
          })
          .sort((a, b) => {
            const rank = p => p.role === "manager" ? 0 : (p.staff_type === "lead" || p.staff_type === "head") ? 1 : 2;
            return rank(a) - rank(b);
          });
        return (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200 }}
            onClick={() => setCoverAssignSheet(null)}
          >
            <div
              style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "white", borderRadius: "20px 20px 0 0", maxHeight: "85vh", overflowY: "auto", display: "flex", flexDirection: "column" }}
              onClick={e => e.stopPropagation()}
            >
              {/* Drag handle */}
              <div style={{ width: 36, height: 4, background: "#ddd", borderRadius: 2, margin: "12px auto 0", flexShrink: 0 }} />
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px 12px", borderBottom: "0.5px solid #EFEFEF", flexShrink: 0 }}>
                <span style={{ fontSize: 17, fontWeight: 700, color: "#F55C00" }}>Assign Coverage</span>
                <button type="button" onClick={() => setCoverAssignSheet(null)} style={{ background: "none", border: "none", fontSize: 15, color: "#888", cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>Cancel</button>
              </div>
              {/* Shift details */}
              <div style={{ padding: "14px 20px", borderBottom: "0.5px solid #EFEFEF", flexShrink: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#111", marginBottom: 4 }}>{r.requester_name} needs coverage</div>
                <div style={{ fontSize: 13, color: "#111", marginBottom: 2 }}>{fmtReqDate(r.shift_date)}</div>
                <div style={{ fontSize: 13, color: "#F55C00", marginBottom: 2, fontWeight: 700 }}>{fmt(r.shift_start)} – {fmt(r.shift_end)}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111", marginBottom: r.reason ? 2 : 0 }}>{r.shift_location}</div>
                {r.reason && <div style={{ fontSize: 12, color: "#111", fontStyle: "italic" }}>{r.reason}</div>}
              </div>
              {/* Section label */}
              <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, padding: "14px 20px 8px", flexShrink: 0 }}>Select Replacement</div>
              {/* Staff list */}
              <div style={{ padding: "0 1px", flex: 1 }}>
                {(() => {
                  if (coverAssignSheet?.selectedEmployeeId) {
                    const selectedHasConflict = shifts.some(s =>
                      s.employee_id === coverAssignSheet.selectedEmployeeId &&
                      s.date === r.shift_date &&
                      s.start_time < r.shift_end &&
                      s.end_time > r.shift_start
                    );
                    if (selectedHasConflict) {
                      setCoverAssignSheet(prev => ({ ...prev, selectedEmployeeId: null }));
                    }
                  }
                  return null;
                })()}
                {sortedProfiles.map(p => {
                  const isManager = p.role === "manager";
                  const isLead = !isManager && (p.staff_type === "lead" || p.staff_type === "head");
                  const badgeLetter = isManager ? "M" : isLead ? "L" : "A";
                  const badgeStyle = isManager
                    ? { background: "#111", border: "2.5px solid #F55C00", color: "#F55C00" }
                    : isLead
                    ? { background: "#F55C00", border: "2.5px solid #111", color: "white" }
                    : { background: "white", border: "2.5px solid #111", color: "#111" };
                  const roleLabel = isManager ? "MNGR" : isLead ? "LEAD" : "ASST";
                  const name = p.first_name && p.last_name ? `${p.first_name} ${p.last_name}` : p.name || "Unknown";
                  const firstName = p.first_name || name.split(" ")[0];
                  const isWorking = shifts.some(s => s.employee_id === p.id && s.date === r.shift_date);
                  const hasConflict = shifts.some(s =>
                    s.employee_id === p.id &&
                    s.date === r.shift_date &&
                    s.start_time < r.shift_end &&
                    s.end_time > r.shift_start
                  );
                  const selected = !hasConflict && coverAssignSheet.selectedEmployeeId === p.id;
                  const AVAIL_DAY_ORDER = [0,1,2,3,4,5,6];
                  const AVAIL_DAY_LABELS = ["MON","TUE","WED","THU","FRI","SAT","SUN"];
                  const fmtAvailTime = t => { const [h, m] = t.split(':'); const hr = parseInt(h); return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`; };
                  return (
                    <div key={p.id}>
                      <div
                        onClick={hasConflict ? () => {} : () => setCoverAssignSheet(prev => ({ ...prev, selectedEmployeeId: prev.selectedEmployeeId === p.id ? null : p.id }))}
                        style={{
                          display: "flex", alignItems: "center", gap: 12, padding: "10px 20px",
                          borderBottom: hasConflict ? "1.5px solid #FFCDD2" : selected && coverAvailability.length > 0 ? "none" : "0.5px solid #EFEFEF",
                          cursor: hasConflict ? "default" : "pointer",
                          border: hasConflict ? "1.5px solid #FFCDD2" : selected ? "2px solid #F55C00" : undefined,
                          borderRadius: selected && coverAvailability.length > 0 ? "12px 12px 0 0" : undefined,
                          background: hasConflict ? "#FFF5F5" : selected ? "#FFF4EE" : "white",
                          WebkitTapHighlightColor: "transparent",
                        }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", fontWeight: 700, fontSize: 11, flexShrink: 0, ...badgeStyle }}>{badgeLetter}</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: hasConflict ? "#999" : "#0A0A0A", flex: 1, display: "flex", alignItems: "center", gap: 6 }}>
                          {name}
                          {hasConflict && <span style={{ fontSize: 10, fontWeight: 700, color: "#E53935", background: "#FFEBEE", borderRadius: 20, padding: "2px 7px" }}>Conflict</span>}
                          {!hasConflict && isWorking && <span style={{ fontSize: 10, fontWeight: 700, color: "#2E7D32", background: "#E8F5E9", borderRadius: 20, padding: "2px 7px", letterSpacing: 0.3 }}>Working</span>}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: isManager ? "#F55C00" : isLead ? "white" : "#111", background: isManager ? "#111" : isLead ? "#F55C00" : "white", border: isManager ? "2px solid #F55C00" : isLead ? "2px solid #111" : "2px solid #111", borderRadius: 20, padding: "3px 10px", flexShrink: 0 }}>{roleLabel}</span>
                      </div>
                      {selected && coverAvailability.length > 0 && (
                        <div style={{ background: "#FFF4EE", border: "2px solid #F55C00", borderTop: "1px solid #F5DCC8", borderRadius: "0 0 12px 12px", padding: "10px 20px 12px" }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#F55C00", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6, fontFamily: "'Oswald', sans-serif" }}>{firstName}'s Availability</div>
                          {AVAIL_DAY_ORDER.map(dow => {
                            const row = coverAvailability.find(av => av.day_of_week === dow);
                            if (!row) return null;
                            return (
                              <div key={dow} style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 0" }}>
                                <span style={{ color: "#111", fontSize: 11, fontWeight: 600, minWidth: 32 }}>{AVAIL_DAY_LABELS[dow]}</span>
                                {row.is_available
                                  ? <span style={{ color: "#111", fontSize: 12, fontWeight: 500 }}>{fmtAvailTime(row.start_time)} – {fmtAvailTime(row.end_time)}</span>
                                  : <span style={{ color: "#999", fontSize: 12 }}>—</span>
                                }
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Confirm button */}
              <div style={{ padding: "16px 20px", borderTop: "0.5px solid #EFEFEF", flexShrink: 0 }}>
                {(() => {
                  const selectedHasConflict = coverAssignSheet.selectedEmployeeId
                    ? shifts.some(s =>
                        s.employee_id === coverAssignSheet.selectedEmployeeId &&
                        s.date === r.shift_date &&
                        s.start_time < r.shift_end &&
                        s.end_time > r.shift_start
                      )
                    : false;
                  const isDisabled = !coverAssignSheet.selectedEmployeeId || selectedHasConflict;
                  return (
                    <button
                      type="button"
                      disabled={isDisabled}
                      onClick={() => {
                        onHandleRequest && onHandleRequest(r.id, "approved", coverAssignSheet.selectedEmployeeId);
                        setCoverAssignSheet(null);
                      }}
                      style={{ width: "100%", padding: "14px 0", borderRadius: 12, border: "none", background: isDisabled ? "#E0E0E0" : "#F55C00", color: isDisabled ? "#999" : "white", fontSize: 15, fontWeight: 700, cursor: isDisabled ? "default" : "pointer", fontFamily: "inherit", transition: "background 0.15s", opacity: isDisabled ? 0.35 : 1 }}
                    >
                      {coverAssignSheet.selectedEmployeeId && !selectedHasConflict
                        ? (() => { const sel = sortedProfiles.find(p => p.id === coverAssignSheet.selectedEmployeeId); const firstName = sel?.first_name || (sel?.name ? sel.name.split(" ")[0] : null) || "Staff"; return `Assign ${firstName} & Approve`; })()
                        : "Assign & Approve"}
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Side drawer (admin) ── */}
      {drawerOpen && ReactDOM.createPortal(
        <AdminDrawer
          user={user}
          allProfiles={allProfiles}
          pendingProfiles={pendingProfiles}
          allLocations={allLocations}
          drawerVisible={drawerVisible}
          onClose={closeDrawer}
          onShiftAdded={onShiftAdded}
          onInvite={onInvite}
          onEditLocations={onEditLocations}
          onEditName={onEditName}
          onChangeRole={onChangeRole}
          onRemove={onRemove}
          onResendInvite={onResendInvite}
        />,
        document.body
      )}

      {/* ── Side drawer (non-admin manager) ── */}
      {drawerOpen && user?.is_admin !== true && ReactDOM.createPortal(
        <>
          {/* Overlay */}
          <div
            onClick={closeDrawer}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
              zIndex: 1000, opacity: drawerVisible ? 1 : 0,
              transition: "opacity 0.28s cubic-bezier(0.4,0,0.2,1)",
            }}
          />
          {/* Panel */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: "fixed", top: 0, right: 0, height: "100%", width: window.innerWidth > 768 ? "33vw" : "72%", maxWidth: window.innerWidth > 768 ? 480 : 320, minWidth: window.innerWidth > 768 ? 360 : "unset",
              background: "white", zIndex: 1001, display: "flex", flexDirection: "column",
              transform: drawerVisible ? "translateX(0)" : "translateX(100%)",
              transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
              boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
              overflow: "hidden",
            }}
          >
            {managerPanel === "home" ? (
              <>
                {/* Header */}
                <div style={{ padding: "56px 24px 20px", borderBottom: "0.5px solid #EFEFEF", flexShrink: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#111", fontFamily: "'Oswald', sans-serif" }}>
                    {user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.name || ""}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#F55C00", fontFamily: "'Oswald', sans-serif", letterSpacing: 0.5, marginTop: 4 }}>MANAGER</div>
                  <div style={{ fontSize: 13, color: user?.phone ? "#111" : "#bbb", marginTop: 6 }}>
                    {user?.phone && user.phone.startsWith("+1") && user.phone.length === 12
                      ? `(${user.phone.slice(2, 5)}) ${user.phone.slice(5, 8)}-${user.phone.slice(8)}`
                      : user?.phone || "No phone number"}
                  </div>
                </div>
                {/* Menu */}
                <div style={{ flex: 1, overflowY: "auto", paddingTop: 8, paddingBottom: 8 }}>
                  {[
                    { icon: "ti-id-badge-2",  label: "My Team",       onClick: () => { const locs = user?.is_admin === true ? allLocations.map(l => l.name) : (user?.locations?.length > 0 ? user.locations : allLocations.map(l => l.name)); setMgrTeamLoc(locs[0] || ""); setManagerPanel("myteam"); } },
                    { icon: "ti-sun",          label: "Time Off",      onClick: () => { setMgrTimeOff({ startDate: "", endDate: "", reason: "", error: "", submitting: false }); setManagerPanel("timeoff"); } },
                    { icon: "ti-bell",         label: "Notifications", onClick: null },
                    { icon: "ti-settings",     label: "Settings",      onClick: null },
                    { icon: "ti-help-circle",  label: "Help",          onClick: null },
                  ].map(({ icon, label, onClick }, i, arr) => (
                    <button
                      key={label}
                      type="button"
                      onClick={onClick || undefined}
                      disabled={!onClick}
                      style={{
                        display: "flex", alignItems: "center", gap: 14, width: "100%",
                        padding: "15px 24px", background: "none", border: "none",
                        borderBottom: i < arr.length - 1 ? "0.5px solid #F5F5F5" : "none",
                        cursor: onClick ? "pointer" : "default", textAlign: "left",
                        WebkitTapHighlightColor: "transparent",
                        opacity: onClick ? 1 : 0.4,
                      }}
                    >
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <i className={`ti ${icon}`} style={{ fontSize: 17, color: onClick ? "#F55C00" : "#999999" }} />
                      </div>
                      <div style={{ flex: 1, fontSize: 14, fontWeight: 500, color: onClick ? "#111" : "#999999", fontFamily: "'Oswald', sans-serif" }}>{label}</div>
                      {onClick
                        ? <i className="ti ti-chevron-right" style={{ fontSize: 14, color: "#CCCCCC", flexShrink: 0 }} />
                        : <span style={{ fontSize: 10, fontWeight: 700, color: "#AAAAAA", fontFamily: "'Oswald', sans-serif", letterSpacing: 0.5, background: "#F0F0F0", borderRadius: 4, padding: "2px 6px", flexShrink: 0 }}>SOON</span>
                      }
                    </button>
                  ))}
                </div>
                {/* Footer */}
                <div style={{ borderTop: "0.5px solid #EFEFEF", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px" }}>
                  <button
                    type="button"
                    onClick={async () => { closeDrawer(); await supabase.auth.signOut(); }}
                    style={{ background: "none", border: "none", padding: 0, fontSize: 13, fontWeight: 700, color: "#E53935", cursor: "pointer", fontFamily: "inherit" }}
                  >Log out</button>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#DDDDDD", fontFamily: "'Oswald', sans-serif", letterSpacing: 1 }}>TMRW</span>
                </div>
              </>
            ) : managerPanel === "timeoff" ? (
              /* ── TIME OFF panel ── */
              (() => {
                const todayStr = new Date().toISOString().slice(0, 10);
                const existingTO = requests.filter(r =>
                  r.type === "timeoff" && (r.status === "pending" || r.status === "approved") && r.requester_id === user.id
                );
                const conflicts = date => existingTO.some(r => date >= r.date && date <= (r.end_date || r.date));
                const endMin = (() => {
                  if (!mgrTimeOff.startDate) return "";
                  const d = new Date(mgrTimeOff.startDate + "T00:00:00");
                  d.setDate(d.getDate() + 1);
                  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                })();
                const submitMgrTimeOff = async () => {
                  if (!mgrTimeOff.startDate || !mgrTimeOff.reason.trim()) {
                    setMgrTimeOff(s => ({ ...s, error: "Start date and reason are required." }));
                    return;
                  }
                  if (mgrTimeOff.startDate < todayStr) {
                    setMgrTimeOff(s => ({ ...s, error: "Start date can't be in the past." }));
                    return;
                  }
                  if (mgrTimeOff.endDate && mgrTimeOff.endDate < mgrTimeOff.startDate) {
                    setMgrTimeOff(s => ({ ...s, error: "End date can't be before start date." }));
                    return;
                  }
                  setMgrTimeOff(s => ({ ...s, submitting: true, error: "" }));
                  const payload = {
                    type: "timeoff", status: "approved",
                    requester_id: user.id, requester_name: user.name,
                    date: mgrTimeOff.startDate,
                    end_date: mgrTimeOff.endDate || mgrTimeOff.startDate,
                    reason: mgrTimeOff.reason.trim(),
                    org_id: user?.org_id || null,
                  };
                  const { error } = await supabase.from("requests").insert(payload);
                  if (error) {
                    setMgrTimeOff(s => ({ ...s, submitting: false, error: "Failed to submit. Try again." }));
                    return;
                  }
                  setMgrTimeOff({ startDate: "", endDate: "", reason: "", error: "", submitting: false });
                  if (eventToastTimer.current) clearTimeout(eventToastTimer.current);
                  setEventToast("Time off saved ✓");
                  eventToastTimer.current = setTimeout(() => setEventToast(null), 4000);
                };
                return (
                  <>
                    {/* Header */}
                    <div style={{ padding: "56px 24px 16px", borderBottom: "0.5px solid #EFEFEF", flexShrink: 0, display: "flex", alignItems: "center", gap: 12 }}>
                      <button type="button" onClick={() => setManagerPanel("home")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
                        <i className="ti ti-arrow-left" style={{ fontSize: 20, color: "#111" }} />
                      </button>
                      <span style={{ fontSize: 16, fontWeight: 700, color: "#111", fontFamily: "'Oswald', sans-serif" }}>Time Off</span>
                    </div>
                    {/* Form */}
                    <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Start Date</div>
                        <input
                          type="date"
                          value={mgrTimeOff.startDate}
                          min={todayStr}
                          onChange={e => {
                            const val = e.target.value;
                            if (val && conflicts(val)) {
                              setMgrTimeOff(s => ({ ...s, startDate: val, endDate: "", error: "This date overlaps with an existing time off request." }));
                            } else {
                              setMgrTimeOff(s => ({ ...s, startDate: val, endDate: "", error: "" }));
                            }
                          }}
                          style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "11px 12px", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box", color: "#111" }}
                        />
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>End Date <span style={{ fontWeight: 400, color: "#aaa", textTransform: "none", letterSpacing: 0 }}>(optional)</span></div>
                        <input
                          type="date"
                          value={mgrTimeOff.endDate}
                          min={endMin}
                          disabled={!mgrTimeOff.startDate}
                          onChange={e => {
                            const val = e.target.value;
                            if (val && conflicts(val)) {
                              setMgrTimeOff(s => ({ ...s, error: "This date overlaps with an existing time off request." }));
                            } else {
                              setMgrTimeOff(s => ({ ...s, endDate: val, error: "" }));
                            }
                          }}
                          style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "11px 12px", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box", color: "#111", opacity: mgrTimeOff.startDate ? 1 : 0.5 }}
                        />
                        <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>Leave blank for a single day</div>
                      </div>
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Reason <span style={{ color: "#E53935" }}>*</span></div>
                        <textarea
                          value={mgrTimeOff.reason}
                          onChange={e => setMgrTimeOff(s => ({ ...s, reason: e.target.value, error: s.error === "Start date and reason are required." ? "" : s.error }))}
                          placeholder="Reason for time off"
                          rows={3}
                          style={{ width: "100%", border: "1px solid #E0E0E0", borderRadius: 10, padding: "11px 12px", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", resize: "none", color: "#111" }}
                        />
                      </div>
                      {mgrTimeOff.error && <div style={{ color: "#E53935", fontSize: 13, marginBottom: 12, fontWeight: 500 }}>{mgrTimeOff.error}</div>}
                      <button
                        type="button"
                        onClick={submitMgrTimeOff}
                        disabled={mgrTimeOff.submitting}
                        style={{ width: "100%", background: mgrTimeOff.submitting ? "#888" : "#F55C00", color: "#ffffff", border: "none", borderRadius: 14, padding: "16px", fontSize: 15, fontWeight: 700, cursor: mgrTimeOff.submitting ? "default" : "pointer", fontFamily: "inherit" }}
                      >{mgrTimeOff.submitting ? "Saving…" : "Save Time Off"}</button>

                      {/* ── Upcoming time off entries ── */}
                      {(() => {
                        const upcoming = requests
                          .filter(r => r.type === "timeoff" && r.requester_id === user.id && (r.status === "approved" || r.status === "pending") && (r.end_date || r.date) >= todayStr)
                          .sort((a, b) => a.date.localeCompare(b.date));
                        if (upcoming.length === 0) return null;
                        return (
                          <div style={{ marginTop: 28 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Upcoming Time Off</div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                              {upcoming.map(r => {
                                const start = fmtShortDate(r.date);
                                const end = r.end_date && r.end_date !== r.date ? fmtShortDate(r.end_date) : null;
                                const dateStr = end ? `${start} – ${end}` : start;
                                return (
                                  <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", borderRadius: 12, background: "#F9F9F9", border: "1px solid #EFEFEF" }}>
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>{dateStr}</div>
                                      {r.reason && <div style={{ fontSize: 12, color: "#F55C00", marginTop: 2, fontFamily: "'Oswald', sans-serif" }}>{r.reason}</div>}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => setMgrTimeOffCancel(r)}
                                      style={{ background: "white", color: "#E53935", fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "5px 10px", border: "1.5px solid #E53935", cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}
                                    >CANCEL</button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    {mgrTimeOffCancel && (
                      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setMgrTimeOffCancel(null)}>
                        <div style={{ background: "white", borderRadius: 16, padding: "24px 20px", width: "calc(100% - 48px)", maxWidth: 320, boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
                          <p style={{ fontSize: 16, fontWeight: 600, color: "#111", marginBottom: 20, lineHeight: 1.4, textAlign: "center" }}>Cancel this time off?</p>
                          <div style={{ display: "flex", gap: 10 }}>
                            <button
                              type="button"
                              onClick={() => setMgrTimeOffCancel(null)}
                              style={{ flex: 1, background: "#F5F5F5", color: "#555", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "10px", border: "none", cursor: "pointer", fontFamily: "inherit" }}
                            >Dismiss</button>
                            <button
                              type="button"
                              onClick={async () => {
                                const id = mgrTimeOffCancel.id;
                                setMgrTimeOffCancel(null);
                                await supabase.from("requests").delete().eq("id", id);
                                if (eventToastTimer.current) clearTimeout(eventToastTimer.current);
                                setEventToast("Time off cancelled");
                                eventToastTimer.current = setTimeout(() => setEventToast(null), 4000);
                              }}
                              style={{ flex: 1, background: "#E53935", color: "white", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "10px", border: "none", cursor: "pointer", fontFamily: "inherit" }}
                            >Cancel Time Off</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()
            ) : (
              /* ── MY TEAM panel ── */
              (() => {
                const mgrLocs = user?.is_admin === true ? allLocations.map(l => l.name) : (user?.locations?.length > 0 ? user.locations : allLocations.map(l => l.name));
                const locOptions = allLocations.length > 0
                  ? allLocations.filter(l => mgrLocs.includes(l.name))
                  : mgrLocs.map(n => ({ name: n }));
                const activeLoc = mgrTeamLoc || mgrLocs[0] || "";
                const locProfiles = allProfiles.filter(p => {
                  if (p.removed === true || p.active === false) return false;
                  if (p.locations && p.locations.length > 0) return p.locations.includes(activeLoc);
                  return (p.location || "") === activeLoc;
                });
                const mgrList   = locProfiles.filter(p => p.role === "manager" && p.is_admin !== true);
                const leadList  = locProfiles.filter(p => (p.staff_type === "lead" || p.staff_type === "head") && p.role !== "manager");
                const asstList  = locProfiles.filter(p => p.role === "employee" && p.staff_type !== "lead" && p.staff_type !== "head");
                const mkBadge = (p) => {
                  if (p.is_admin === true) return { letter: "AD", style: { background: "#6B21A8", border: "2.5px solid #D8B4FE", color: "#D8B4FE" } };
                  if (p.role === "manager") return { letter: "M", style: { background: "#111", border: "2.5px solid #F55C00", color: "#F55C00" } };
                  if (p.staff_type === "lead" || p.staff_type === "head") return { letter: "L", style: { background: "#F55C00", border: "2.5px solid #111", color: "white" } };
                  return { letter: "A", style: { background: "white", border: "2.5px solid #111", color: "#111" } };
                };
                const displayName = (p) => p.first_name && p.last_name ? `${p.first_name} ${p.last_name}` : p.name || "Unnamed";
                return (
                  <>
                    {/* Panel header */}
                    <div style={{ padding: "56px 20px 16px", borderBottom: "0.5px solid #EFEFEF", flexShrink: 0, display: "flex", alignItems: "center", gap: 12 }}>
                      <button
                        type="button"
                        onClick={() => setManagerPanel("home")}
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", color: "#F55C00", WebkitTapHighlightColor: "transparent", flexShrink: 0 }}
                      >
                        <i className="ti ti-chevron-left" style={{ fontSize: 24 }} />
                      </button>
                      <span style={{ fontSize: 16, fontWeight: 700, color: "#111", fontFamily: "'Oswald', sans-serif", letterSpacing: 0.5, flex: 1 }}>MY TEAM</span>
                      <button
                        type="button"
                        onClick={() => onInvite()}
                        style={{ width: 32, height: 32, borderRadius: "50%", background: "#F55C00", border: "none", color: "white", fontSize: 22, fontWeight: 300, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, lineHeight: 1, WebkitTapHighlightColor: "transparent" }}
                      >+</button>
                    </div>
                    {/* Scrollable content */}
                    <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "20px 20px calc(40px + env(safe-area-inset-bottom, 34px))" }}>
                      {/* Location filter */}
                      {locOptions.length > 1 && (
                        <select
                          value={activeLoc}
                          onChange={e => setMgrTeamLoc(e.target.value)}
                          style={{ width: "100%", padding: "10px 14px", background: "#f5f5f5", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 600, color: "#111", cursor: "pointer", appearance: "none", WebkitAppearance: "none", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23111' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center", fontFamily: "inherit", marginBottom: 20 }}
                        >
                          {locOptions.map(l => <option key={l.name} value={l.name}>{l.name}</option>)}
                        </select>
                      )}
                      {/* MANAGERS */}
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#111", letterSpacing: 1, marginBottom: 8, fontFamily: "'Oswald', sans-serif" }}>MANAGERS</div>
                      {mgrList.length === 0 && <div style={{ fontSize: 13, color: "#aaa", paddingBottom: 8 }}>No managers at this location.</div>}
                      {mgrList.map(p => { const b = mkBadge(p); return (
                        <div key={p.id} onClick={() => setContactCard(p)} style={{ borderBottom: "1px solid #EFEFEF", padding: "14px 0", display: "flex", alignItems: "center", gap: 10, cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", fontWeight: 700, fontSize: b.letter === "AD" ? 9 : 12, flexShrink: 0, ...b.style }}>{b.letter}</span>
                          <span style={{ fontWeight: 600, color: "#111", fontSize: 15 }}>{displayName(p)}</span>
                        </div>
                      ); })}
                      {/* LEADS */}
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#111", letterSpacing: 1, margin: "20px 0 8px", fontFamily: "'Oswald', sans-serif" }}>LEADS</div>
                      {leadList.length === 0 && <div style={{ fontSize: 13, color: "#aaa", paddingBottom: 8 }}>No leads at this location.</div>}
                      {leadList.map(p => { const b = mkBadge(p); return (
                        <div key={p.id} style={{ borderBottom: "1px solid #EFEFEF", padding: "14px 0", display: "flex", alignItems: "center", gap: 10 }}>
                          <div onClick={() => setContactCard(p)} style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", fontWeight: 700, fontSize: b.letter === "AD" ? 9 : 12, flexShrink: 0, ...b.style }}>{b.letter}</span>
                            <span style={{ fontWeight: 600, color: "#111", fontSize: 15 }}>{displayName(p)}</span>
                          </div>
                          <div style={{ position: "relative" }}>
                            {mgrDrawerMenuOpen === p.id && <div onClick={() => setMgrDrawerMenuOpen(null)} style={{ position: "fixed", inset: 0, zIndex: 999 }} />}
                            <button
                              type="button"
                              onClick={() => setMgrDrawerMenuOpen(mgrDrawerMenuOpen === p.id ? null : p.id)}
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#F55C00", padding: "4px 8px", fontFamily: "inherit", lineHeight: 1 }}
                            >···</button>
                            {mgrDrawerMenuOpen === p.id && (
                              <div style={{ position: "absolute", right: 0, top: "100%", background: "white", borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", zIndex: 1000, minWidth: 200, border: "0.5px solid #F0F0F0", overflow: "hidden" }}>
                                <button type="button" onClick={() => { setMgrDrawerMenuOpen(null); onEditName(p); }}
                                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "13px 16px", background: "none", border: "none", borderBottom: "0.5px solid #F5F5F5", fontSize: 14, fontWeight: 500, color: "#111", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                                  <i className="ti ti-pencil" style={{ fontSize: 16, color: "#AAAAAA", flexShrink: 0 }} />Edit Name
                                </button>
                                <button type="button" onClick={() => { setMgrDrawerMenuOpen(null); onEditLocations(p); }}
                                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "13px 16px", background: "none", border: "none", borderBottom: "0.5px solid #F5F5F5", fontSize: 14, fontWeight: 500, color: "#111", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                                  <i className="ti ti-map-pin" style={{ fontSize: 16, color: "#AAAAAA", flexShrink: 0 }} />Edit Locations
                                </button>
                                <div style={{ height: "0.5px", background: "#EFEFEF" }} />
                                <button type="button" onClick={() => { setMgrDrawerMenuOpen(null); onChangeRole(p, "Assistant"); }}
                                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "13px 16px", background: "none", border: "none", borderBottom: "0.5px solid #F5F5F5", fontSize: 14, fontWeight: 500, color: "#E53935", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                                  <i className="ti ti-arrow-down" style={{ fontSize: 16, color: "#E53935", flexShrink: 0 }} />Make Assistant
                                </button>
                                <div style={{ height: "0.5px", background: "#EFEFEF" }} />
                                <button type="button" onClick={() => { setMgrDrawerMenuOpen(null); onRemove(p); }}
                                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "13px 16px", background: "none", border: "none", fontSize: 14, fontWeight: 500, color: "#E53935", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                                  <i className="ti ti-user-x" style={{ fontSize: 16, color: "#E53935", flexShrink: 0 }} />Remove
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ); })}
                      {/* ASSISTANTS */}
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#111", letterSpacing: 1, margin: "20px 0 8px", fontFamily: "'Oswald', sans-serif" }}>ASSISTANTS</div>
                      {asstList.length === 0 && <div style={{ fontSize: 13, color: "#aaa", paddingBottom: 8 }}>No assistants at this location.</div>}
                      {asstList.map(p => { const b = mkBadge(p); return (
                        <div key={p.id} style={{ borderBottom: "1px solid #EFEFEF", padding: "14px 0", display: "flex", alignItems: "center", gap: 10 }}>
                          <div onClick={() => setContactCard(p)} style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, cursor: "pointer", WebkitTapHighlightColor: "transparent" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", fontWeight: 700, fontSize: b.letter === "AD" ? 9 : 12, flexShrink: 0, ...b.style }}>{b.letter}</span>
                            <span style={{ fontWeight: 600, color: "#111", fontSize: 15 }}>{displayName(p)}</span>
                          </div>
                          <div style={{ position: "relative" }}>
                            {mgrDrawerMenuOpen === p.id && <div onClick={() => setMgrDrawerMenuOpen(null)} style={{ position: "fixed", inset: 0, zIndex: 999 }} />}
                            <button
                              type="button"
                              onClick={() => setMgrDrawerMenuOpen(mgrDrawerMenuOpen === p.id ? null : p.id)}
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#F55C00", padding: "4px 8px", fontFamily: "inherit", lineHeight: 1 }}
                            >···</button>
                            {mgrDrawerMenuOpen === p.id && (
                              <div style={{ position: "absolute", right: 0, top: "100%", background: "white", borderRadius: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", zIndex: 1000, minWidth: 200, border: "0.5px solid #F0F0F0", overflow: "hidden" }}>
                                <button type="button" onClick={() => { setMgrDrawerMenuOpen(null); onEditName(p); }}
                                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "13px 16px", background: "none", border: "none", borderBottom: "0.5px solid #F5F5F5", fontSize: 14, fontWeight: 500, color: "#111", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                                  <i className="ti ti-pencil" style={{ fontSize: 16, color: "#AAAAAA", flexShrink: 0 }} />Edit Name
                                </button>
                                <button type="button" onClick={() => { setMgrDrawerMenuOpen(null); onEditLocations(p); }}
                                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "13px 16px", background: "none", border: "none", borderBottom: "0.5px solid #F5F5F5", fontSize: 14, fontWeight: 500, color: "#111", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                                  <i className="ti ti-map-pin" style={{ fontSize: 16, color: "#AAAAAA", flexShrink: 0 }} />Edit Locations
                                </button>
                                <div style={{ height: "0.5px", background: "#EFEFEF" }} />
                                <button type="button" onClick={() => { setMgrDrawerMenuOpen(null); onChangeRole(p, "Lead"); }}
                                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "13px 16px", background: "none", border: "none", borderBottom: "0.5px solid #F5F5F5", fontSize: 14, fontWeight: 500, color: "#22C55E", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                                  <i className="ti ti-arrow-up" style={{ fontSize: 16, color: "#22C55E", flexShrink: 0 }} />Make Lead
                                </button>
                                <div style={{ height: "0.5px", background: "#EFEFEF" }} />
                                <button type="button" onClick={() => { setMgrDrawerMenuOpen(null); onRemove(p); }}
                                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "13px 16px", background: "none", border: "none", fontSize: 14, fontWeight: 500, color: "#E53935", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                                  <i className="ti ti-user-x" style={{ fontSize: 16, color: "#E53935", flexShrink: 0 }} />Remove
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ); })}
                      {/* PENDING */}
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#111", letterSpacing: 1, margin: "20px 0 8px", fontFamily: "'Oswald', sans-serif" }}>PENDING</div>
                      {pendingProfiles.length === 0
                        ? <div style={{ fontSize: 13, color: "#aaa", paddingBottom: 8 }}>No pending invites.</div>
                        : pendingProfiles.map(p => (
                          <div key={p.id} style={{ borderBottom: "1px solid #EFEFEF", padding: "14px 0" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", fontWeight: 700, fontSize: 12, flexShrink: 0, opacity: 0.5, background: p.staff_type === "lead" ? "#F55C00" : "white", border: "2.5px solid #111", color: p.staff_type === "lead" ? "white" : "#111" }}>
                                  {p.staff_type === "lead" ? "L" : "A"}
                                </span>
                                <div>
                                  <div style={{ fontSize: 15, fontWeight: 600, color: "#111" }}>{displayName(p)}</div>
                                  <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>{p.email}</div>
                                </div>
                              </div>
                              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                                <button type="button" onClick={() => onResendInvite(p)} style={{ background: "#F55C00", color: "white", border: "none", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Resend</button>
                                <button type="button" onClick={() => { console.log("[cancel-invite] cancel button tapped, setting confirm to:", p); setCancelInviteConfirm(p); }} style={{ background: "white", border: "1px solid #E0E0E0", borderRadius: 20, padding: "6px 14px", color: "#E53935", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                              </div>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </>
                );
              })()
            )}
          </div>
        </>,
        document.body
      )}

      {/* ── Contact Card modal ── */}
      {contactCard && ReactDOM.createPortal(
        <div
          onClick={() => setContactCard(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: "white", borderRadius: 20, width: "100%", maxWidth: 340, padding: "28px 24px 24px", position: "relative" }}>
            <button type="button" onClick={() => setContactCard(null)}
              style={{ position: "absolute", top: 14, right: 14, background: "none", border: "none", fontSize: 18, color: "#aaa", cursor: "pointer", lineHeight: 1 }}>✕</button>
            {(() => {
              const p = contactCard;
              const b = (() => {
                if (p.is_admin === true) return { letter: "AD", label: "ADMIN", style: { background: "#6B21A8", border: "3px solid #D8B4FE", color: "#D8B4FE" }, pillBg: "#6B21A8", pillColor: "#D8B4FE" };
                if (p.role === "manager") return { letter: "M", label: "MANAGER", style: { background: "#111", border: "3px solid #F55C00", color: "#F55C00" }, pillBg: "#111", pillColor: "#F55C00" };
                if (p.staff_type === "lead" || p.staff_type === "head") return { letter: "L", label: "LEAD", style: { background: "#F55C00", border: "3px solid #111", color: "white" }, pillBg: "#F55C00", pillColor: "white" };
                return { letter: "A", label: "ASSISTANT", style: { background: "white", border: "3px solid #111", color: "#111" }, pillBg: "#111", pillColor: "white" };
              })();
              const fullName = p.first_name && p.last_name ? `${p.first_name} ${p.last_name}` : p.name || "Unnamed";
              const locs = p.locations && p.locations.length > 0 ? p.locations.join(", ") : p.location || "";
              const email = p.email || "";
              const phone = p.phone || "";
              const phoneDisplay = phone.startsWith("+1") && phone.length === 12
                ? `(${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`
                : phone;
              return (
                <>
                  {/* Badge + name + role */}
                  <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 48, height: 48, borderRadius: "50%", fontWeight: 700, fontSize: b.letter === "AD" ? 16 : 22, flexShrink: 0, ...b.style }}>{b.letter}</span>
                    <div>
                      <div style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 20, color: "#111", lineHeight: 1.2 }}>{fullName}</div>
                      <span style={{ display: "inline-block", marginTop: 4, fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "3px 10px", borderRadius: 20, background: b.pillBg, color: b.pillColor, fontFamily: "'Oswald', sans-serif" }}>{b.label}</span>
                    </div>
                  </div>
                  {/* Location */}
                  {locs && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: "1px solid #F0F0F0" }}>
                      <i className="ti ti-map-pin" style={{ fontSize: 18, color: "#F55C00", flexShrink: 0 }} />
                      <span style={{ fontSize: 14, color: "#111" }}>{locs}</span>
                    </div>
                  )}
                  {/* Email */}
                  {email && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: "1px solid #F0F0F0" }}>
                      <i className="ti ti-mail" style={{ fontSize: 18, color: "#F55C00", flexShrink: 0 }} />
                      <a href={`mailto:${email}`} style={{ fontSize: 14, color: "#111", textDecoration: "none", flex: 1 }}>{email}</a>
                    </div>
                  )}
                  {/* Phone */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: "1px solid #F0F0F0" }}>
                    <i className="ti ti-message" style={{ fontSize: 18, color: "#F55C00", flexShrink: 0 }} />
                    {phone ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
                        <a href={`sms:${phone}`} style={{ fontSize: 14, color: "#111", textDecoration: "none" }}>{phoneDisplay}</a>
                        <a href={`tel:${phone}`} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", background: "#F55C00", color: "white", flexShrink: 0, textDecoration: "none" }}>
                          <i className="ti ti-phone" style={{ fontSize: 14 }} />
                        </a>
                      </div>
                    ) : (
                      <span style={{ fontSize: 14, color: "#bbb", fontStyle: "italic" }}>No number</span>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>,
        document.body
      )}

      {/* ── Edit Shift bottom sheet ── */}
      {editShiftTarget && (
        <div
          className="fixed inset-0"
          style={{ background: "rgba(0,0,0,0.5)", zIndex: 1000 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeEditShift(); }}
          onTouchStart={(e) => { if (e.target === e.currentTarget) closeEditShift(); }}
        >
          <div
            className="fixed bottom-0 left-0 right-0"
            style={{ background: "white", borderRadius: "20px 20px 0 0", maxHeight: "60vh", display: "flex", flexDirection: "column", transform: editShiftVisible ? "translateY(0)" : "translateY(100%)", transition: "transform 300ms ease", zIndex: 1001 }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div style={{ width: 36, height: 4, background: "#E0E0E0", borderRadius: 2, margin: "10px auto 4px" }} />

            {/* Header */}
            <div style={{ padding: "10px 20px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #EFEFEF", flexShrink: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 17, fontWeight: 700, color: "#F55C00" }}>
                    {(() => {
                      const p = allProfiles.find(x => x.id === editShiftTarget.employee_id);
                      return p ? (p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim()) : editShiftTarget.employee_name || "Edit Shift";
                    })()}
                  </span>
                  {editShiftTarget.scheduled_by && (
                    <span style={{ fontSize: 11, color: "#111", fontStyle: "italic" }}>Scheduled by {editShiftTarget.scheduled_by}</span>
                  )}
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#111" }}>Adjust Shift Time</span>
              </div>
              <button type="button" onClick={closeEditShift} style={{ background: "none", border: "none", fontSize: 15, color: "#888", cursor: "pointer", fontFamily: "inherit", fontWeight: 500 }}>Cancel</button>
            </div>

            {/* Body */}
            <div style={{ overflowY: "auto", flex: 1, padding: "20px 20px 48px", WebkitOverflowScrolling: "touch" }}>
              <div style={{ marginBottom: 8, fontSize: 12, color: "#111" }}>
                {fmtReqDate(editShiftTarget.date)}
              </div>

              {/* TIME */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#111", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                  Time
                </div>
                {editShiftError && (
                  <p style={{ color: "#E53935", fontSize: 13, marginBottom: 8 }}>{editShiftError}</p>
                )}
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <DrumPicker label="Start" value={editShiftTimes.start_time} onChange={v => {
                      setEditShiftTimes(t => ({ ...t, start_time: v, end_time: v >= t.end_time ? addHour(v) : t.end_time }));
                    }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <DrumPicker label="End" value={editShiftTimes.end_time} onChange={v => setEditShiftTimes(t => ({ ...t, end_time: v }))} />
                  </div>
                </div>
              </div>

              {/* Save */}
              <button
                type="button"
                onClick={submitEditShift}
                disabled={editShiftSubmitting}
                style={{ width: "100%", background: editShiftSubmitting ? "#f5a87a" : "#F55C00", color: "white", border: "none", borderRadius: 14, padding: "16px", fontSize: 15, fontWeight: 700, cursor: editShiftSubmitting ? "default" : "pointer", fontFamily: "inherit" }}
              >
                {editShiftSubmitting ? "Saving…" : "SAVE CHANGES"}
              </button>
            </div>
          </div>

          {/* ── Conflict modal ── */}
          {editShiftConflict && (
            <div
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 24px" }}
              onClick={() => setEditShiftConflict(null)}
            >
              <div
                style={{ background: "white", borderRadius: 16, padding: "28px 24px", width: "100%", maxWidth: 340 }}
                onClick={e => e.stopPropagation()}
              >
                <p style={{ fontSize: 15, fontWeight: 500, color: "#111", marginBottom: 24, lineHeight: 1.55 }}>
                  <span style={{ color: "#F55C00" }}>{editShiftConflict.name} is assigned to {editShiftConflict.event.title} at {fmt(editShiftConflict.event.start_time)}{editShiftConflict.event.end_time ? `–${fmt(editShiftConflict.event.end_time)}` : ""}.</span>{" "}Remove them from the event to save these shift times.
                </p>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => setEditShiftConflict(null)}
                    style={{ flex: 1, background: "white", color: "#555", fontSize: 13, fontWeight: 600, borderRadius: 20, padding: "11px", border: "1.5px solid #DDD", cursor: "pointer", fontFamily: "inherit" }}
                  >Cancel</button>
                  <button
                    type="button"
                    onClick={doRemoveAndSave}
                    style={{ flex: 1, background: "#111", color: "white", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "11px", border: "none", cursor: "pointer", fontFamily: "inherit" }}
                  >Remove &amp; Save</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Event toast ── */}
      {eventToast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "white", border: "0.5px solid #E0E0E0", borderLeft: "3.5px solid #F55C00",
          borderRadius: 14, padding: "14px 18px", zIndex: 9999, maxWidth: 360, width: "calc(100vw - 48px)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#111", lineHeight: 1.5 }}>
            {eventToast}
          </div>
        </div>
      )}

      {/* ── Shift toast ── */}
      {shiftToast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "white", border: "0.5px solid #E0E0E0", borderLeft: "3.5px solid #F55C00",
          borderRadius: 14, padding: "14px 18px", zIndex: 9999, maxWidth: 360, width: "calc(100vw - 48px)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#111", lineHeight: 1.5 }}>
            {shiftToast.successLine}
          </div>
          {shiftToast.conflictLines.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {shiftToast.conflictLines.map((line, i) => (
                <div key={i} style={{ fontSize: 13, color: "#E24B4A", lineHeight: 1.5, marginTop: 2 }}>{line}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Revoke confirmation modal ── */}
      {revokeTarget && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 24px" }}
          onClick={() => setRevokeTarget(null)}
        >
          <div
            style={{ background: "white", borderRadius: 16, padding: "28px 24px", width: "100%", maxWidth: 340 }}
            onClick={e => e.stopPropagation()}
          >
            <p style={{ fontSize: 16, fontWeight: 600, color: "#111", marginBottom: 8, lineHeight: 1.4 }}>
              Revoke {revokeTarget.requester_name}'s approved time off?
            </p>
            <p style={{ fontSize: 13, color: "#666", marginBottom: 24, lineHeight: 1.4 }}>
              This will move the request back to pending.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setRevokeTarget(null)}
                style={{ flex: 1, background: "white", color: "#111", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "10px", border: "1.5px solid #111", cursor: "pointer", fontFamily: "inherit" }}
              >CANCEL</button>
              <button
                onClick={async () => {
                  const id = revokeTarget.id;
                  setRevokeTarget(null);
                  await supabase.from("requests").update({ status: "pending" }).eq("id", id);
                  onShiftAdded?.();
                }}
                style={{ flex: 1, background: "#E53935", color: "white", fontSize: 13, fontWeight: 700, borderRadius: 20, padding: "10px", border: "none", cursor: "pointer", fontFamily: "inherit" }}
              >REVOKE</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
